import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { database, auth, firestore, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { ref, update, onValue, push, set, remove, get } from "firebase/database";
import { collection, query, where, getDocs, writeBatch, doc, onSnapshot, addDoc, updateDoc, deleteDoc, orderBy, limit, getCountFromServer } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { CREDIT_PLANS, EMAIL_PLAN_LIMITS } from "../config/creditPlans";
import WorldSettingsTopics from "./WorldSettingsTopics";
import ImageLab from "./ImageLab";
import SalesStudio from "./SalesStudio";
import WhatsappSettings from "./WhatsappSettings";
import WhisperSettings from "./WhisperSettings";
import AgentsSettings from "./AgentsSettings";
import WorldSettingsCockpit from "./WorldSettingsCockpit";
import MoreWorldSettings from "./MoreWorldSettings";
import SafeGuardSettings from "./SafeGuardSettings";
import SpamCheckSettings from "./SpamCheckSettings";
import TurboAiSetup from "./TurboAiSetup";
import BlackList from "./BlackList";
import LeadSettings from "./LeadSettings";
import { ResearchSettingsPage } from "./LeadSpySettings";
import AiStudioOverview from "./AiStudioOverview";
import EmailOutreachOverview from "./EmailOutreachOverview";
import AnalyticsSettings from "./AnalyticsSettings";
import TeamSettings from "./TeamSettings";
import ApiSettings from "./ApiSettings";
import CustomFieldsSettings from "./CustomFieldsSettings";
import AutoForwardSettings from "./AutoForwardSettings";
import CrmIntegrationsSettings from "./CrmIntegrationsSettings";
import DataConnectorsSettings from "./DataConnectorsSettings";
import DataExportSettings from "./DataExportSettings";
import OutreachJobsSettings from "./OutreachJobsSettings";
import CopyCloneAI from "./CopyCloneAI/CopyCloneAI";
import { useActiveWorld } from "../hooks/useActiveWorld";
import ConfirmModal from "./ConfirmModal";
import "./WorldSettings.css";
import "./AdvancedSettings.css";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { generateEmailFooter } from './EmailFooterGenerator';
import FooterDeliverabilityChecker, { analyzeFooter } from './FooterDeliverabilityChecker';
import { FOOTER_TEMPLATES, buildFooterFromTemplate, isFooterMeaningful } from './footerTemplates';
import downArrow from '../img/down.svg';
import { encryptEmailPassword, isPasswordEncrypted } from '../utils/encryption';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import TrueWarmup from './TrueWarmup';
import SettingsTabNav from './SettingsTabNav';

// Footer-Logo Upload-Grenze. Das Bild wird beim Versand INLINE in jede E-Mail
// eingebettet (siehe functions/helpers/footerImageEmbed.js) — es reist also in
// jeder Mail mit. Darum halten wir es klein: erst automatisch komprimieren,
// dann hart bei 200 KB kappen.
const FOOTER_IMAGE_MAX_BYTES = 200 * 1024;

/**
 * Komprimiert/verkleinert ein Footer-Logo VOR dem Upload, damit jede E-Mail
 * die das Logo einbettet klein bleibt.
 *   - PNG behält Transparenz (Logos), wird nur in den Dimensionen verkleinert.
 *   - JPEG wird mit gestufter Qualität neu kodiert.
 *   - Animierte GIFs werden NICHT angefasst (Canvas würde sie zu einem
 *     Einzelbild plätten) — hier greift nur die harte Größengrenze im Aufrufer.
 * Fällt bei jedem Fehler auf die Originaldatei zurück (never-throw).
 *
 * @returns {Promise<File>} die (ggf.) komprimierte Datei
 */
const compressFooterImage = (file, { maxBytes = FOOTER_IMAGE_MAX_BYTES, maxDimension = 800 } = {}) =>
  new Promise((resolve) => {
    try {
      if (!file || !file.type || file.type === 'image/gif') {
        resolve(file);
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = async () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const isPng = file.type === 'image/png';
          const mime = isPng ? 'image/png' : 'image/jpeg';

          const encode = (canvas, quality) =>
            new Promise((res) => canvas.toBlob(res, mime, quality));

          const render = (targetW, targetH) => {
            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, targetW, targetH);
            return canvas;
          };

          const baseScale = Math.min(1, maxDimension / Math.max(img.width, img.height));
          let width = Math.max(1, Math.round(img.width * baseScale));
          let height = Math.max(1, Math.round(img.height * baseScale));

          let best = null;
          for (let attempt = 0; attempt < 6; attempt++) {
            const canvas = render(width, height);
            // JPEG: Qualität pro Runde senken; PNG ignoriert den Quality-Param.
            const quality = isPng ? undefined : Math.max(0.4, 0.9 - attempt * 0.1);
            // eslint-disable-next-line no-await-in-loop
            const blob = await encode(canvas, quality);
            if (!blob) break;
            best = blob;
            if (blob.size <= maxBytes) break;
            // Für die nächste Runde Dimensionen um ~15 % verkleinern.
            width = Math.max(1, Math.round(width * 0.85));
            height = Math.max(1, Math.round(height * 0.85));
          }

          if (!best) {
            resolve(file);
            return;
          }

          const ext = isPng ? 'png' : 'jpg';
          const baseName = (file.name || 'logo').replace(/\.[^.]+$/, '');
          const compressed = new File([best], `${baseName}.${ext}`, { type: mime });
          // Nur übernehmen, wenn wir tatsächlich kleiner sind.
          resolve(compressed.size < file.size ? compressed : file);
        } catch (_) {
          resolve(file);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };

      img.src = objectUrl;
    } catch (_) {
      resolve(file);
    }
  });

// Gültige Tab-IDs für URL-Validierung (dashboard = Cockpit als erster Screen)
const VALID_TABS = [
  'dashboard', 'ai', 'email', 'emailPreferences', 'copyClones', 'topics', 'trueWarmup', 'whatsapp', 'whisper',
  'dangerZone', 'agents', 'extraSettings', 'safeGuard', 'spamAnalyzer',
  'blacklist', 'leadSettings', 'research', 'outreachJobs', 'analytics', 'team', 'api', 'customFields',
  'crmIntegrations', 'dataExport', 'imageLab', 'salesStudio', 'autoForward', 'aiStudio', 'emailOutreach',
  'dataSources'
];

// Email Tabs (benötigen expandierte Sidebar). 'emailOutreach' ist die
// Übersicht der Gruppe, analog zu 'aiStudio'.
const EMAIL_TABS = ['email', 'emailPreferences', 'copyClones', 'topics', 'trueWarmup', 'emailOutreach', 'agents'];

// "Email Setup" bündelt drei Panes hinter EINEM Sidebar-Eintrag. `activeTab`
// bleibt der jeweilige Sub-Tab, damit bestehende Deep-Links (?tab=trueWarmup)
// und `onOpenTab` aus dem Cockpit unverändert funktionieren — die Sidebar
// zeigt einfach die Gruppe als aktiv, statt drei Zeilen zu verbrauchen.
const EMAIL_SETUP_TABS = [
  { id: 'email', label: 'Accounts', icon: 'fa-inbox' },
  { id: 'emailPreferences', label: 'Footer & Signature', icon: 'fa-signature' },
  { id: 'trueWarmup', label: 'Warmup', icon: 'fa-fire' },
];
const EMAIL_SETUP_TAB_IDS = EMAIL_SETUP_TABS.map(t => t.id);

// "Templates" bündelt die Vorlagen und CopyTwin. Das AI-Badge zieht mit in die
// Pane-Nav, damit CopyTwin seine Kennzeichnung behält, obwohl es keine eigene
// Sidebar-Zeile mehr belegt.
const TEMPLATE_TABS = [
  { id: 'topics', label: 'Templates', icon: 'fa-file-lines' },
  { id: 'copyClones', label: 'CopyTwin', icon: 'fa-wand-magic-sparkles', badge: 'AI' },
];
const TEMPLATE_TAB_IDS = TEMPLATE_TABS.map(t => t.id);

// Agents & Flows Tabs (benötigen expandierte Sidebar). 'aiStudio' ist die
// Übersichtsseite der Gruppe: sie hat bewusst keine eigene Sidebar-Zeile,
// sondern gehört dem Parent selbst — Klick auf "AI Studio" klappt auf UND zeigt
// die Karten aller Tools rechts an.
const AGENTS_FLOWS_TABS = ['leadSettings', 'research', 'outreachJobs', 'imageLab', 'salesStudio', 'aiStudio'];

// Sales Studio bündelt alle Plays; 'imageLab' bleibt als Deep-Link und landet
// im Play Store, wo jedes Tool seine eigene App ist.
const SALES_STUDIO_TAB_IDS = ['salesStudio', 'imageLab'];

// Safety bündelt SafeGuard, BlackList und Spam Analyzer hinter EINEM
// Sidebar-Eintrag — gleiche Pane-Nav wie Email Setup / Templates.
const SAFETY_TABS = [
  { id: 'safeGuard', label: 'SafeGuard', icon: 'fa-shield-halved' },
  { id: 'blacklist', label: 'BlackList', icon: 'fa-ban' },
  { id: 'spamAnalyzer', label: 'Spam Analyzer', icon: 'fa-shield-virus' },
];
const SAFETY_TAB_IDS = SAFETY_TABS.map(t => t.id);

// Admin → Advanced Settings bündelt Auto-Forward, Data Export und die
// bisherigen Automatisierungsschalter hinter EINEM Sidebar-Eintrag — gleiche
// 3er-Pane-Nav wie Safety. IDs bleiben, damit Deep-Links (?tab=autoForward,
// ?tab=dataExport, ?tab=ai) und `onOpenTab('ai')` weiter zielen.
const ADVANCED_SETTINGS_TABS = [
  { id: 'autoForward', label: 'Auto-Forward', icon: 'fa-share', ownerOnly: true },
  { id: 'dataExport', label: 'Data Export', icon: 'fa-file-export', ownerOnly: true },
  { id: 'customFields', label: 'Custom Fields', icon: 'fa-tags' },
  { id: 'ai', label: 'Advanced Settings', icon: 'fa-sliders' },
];
const ADVANCED_SETTINGS_TAB_IDS = ADVANCED_SETTINGS_TABS.map(t => t.id);

// Admin Tabs. 'ai' ist die frühere Inbox-Pane: die Aktivierung der Inbox läuft
// von selbst, übrig blieben Automatisierungsschalter — die gehören unter Admin →
// Advanced Settings, nicht in einen eigenen Sidebar-Eintrag.
const ADMIN_TABS = ['analytics', 'team', 'customFields', 'dataExport', 'autoForward', 'ai', 'dangerZone'];

// Admin → Integrations bündelt API, CRM, Calendly und WhatsApp hinter EINEM
// Sidebar-Eintrag mit derselben 4er-Pane-Nav wie Safety/Advanced Settings.
// (WhatsApp ist streng genommen kein Integration-Setting, sitzt aus UX-Gründen
// aber hier — Advanced Settings hätte sonst nur noch Automation-Toggles.)
const INTEGRATIONS_TABS = [
  { id: 'api', label: 'API', icon: 'fa-plug' },
  { id: 'dataSources', label: 'Data Sources', icon: 'fa-database' },
  { id: 'crmIntegrations', label: 'CRM', icon: 'fa-network-wired' },
  { id: 'extraSettings', label: 'Calendly', icon: 'fa-calendar-days' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'fa-comments' },
];
const INTEGRATIONS_TAB_IDS = INTEGRATIONS_TABS.map(t => t.id);
// whisper/screenComposer bleiben als Deep-Link-Ziele erhalten, obwohl sie
// nicht mehr in der Sidebar sichtbar sind.
const INTEGRATION_TABS = [...INTEGRATIONS_TAB_IDS, 'whisper', 'screenComposer'];

// Gemeinsame Slide-In/Out-Animation für die ausklappbaren Sidebar-Gruppen.
// Höhe federt elegant auf/zu, Inhalt blendet sanft mit ein. Auf Mobile sorgt
// `.settingsSubGroup` (display: contents) dafür, dass das Row-Layout erhalten bleibt.
const SUBNAV_MOTION = {
  className: 'settingsSubGroup',
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  // Tween (kein Spring): Springs überschwingen die Endhöhe und „nachrucken"
  // dann zurück, was bei overflow:hidden als Ruckeln am Schluss sichtbar wird.
  // Eine sanfte ease-Kurve fährt sauber auf die Zielhöhe ohne Overshoot.
  transition: {
    height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    opacity: { duration: 0.2, ease: 'easeOut' },
  },
  style: { overflow: 'hidden' },
};

const WorldSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab aus URL lesen oder 'dashboard' (Cockpit) als Default
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'dashboard';
  };
  
  const [activeTab, setActiveTabState] = useState(getInitialTab);
  const [emailExpanded, setEmailExpanded] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return EMAIL_TABS.includes(tabFromUrl);
  });
  const [agentsFlowsExpanded, setAgentsFlowsExpanded] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return AGENTS_FLOWS_TABS.includes(tabFromUrl);
  });
  const [adminExpanded, setAdminExpanded] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return ADMIN_TABS.includes(tabFromUrl) || INTEGRATION_TABS.includes(tabFromUrl);
  });
  const [integrationsExpanded, setIntegrationsExpanded] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return INTEGRATION_TABS.includes(tabFromUrl);
  });
  // Team System: Hole die aktive World-ID (eigene oder Team-Owner's)
  const { userId: activeWorldUserId, isOwner, currentUserId, hasFeature, canWrite, canAccessSettings, accountType, isLoading: isTeamLoading } = useActiveWorld();
  const userId = activeWorldUserId; // Verwende die aktive World-ID für alle Daten
  
  // 🔒 SICHERHEIT: Prüfe ob User Settings-Berechtigung hat (wird später im Return verwendet)
  const hasSettingsAccess = canAccessSettings();
  
  const openNavSection = useCallback((section) => {
    setEmailExpanded(section === 'email');
    setAgentsFlowsExpanded(section === 'agentsFlows');
    setAdminExpanded(section === 'admin');
    if (section !== 'admin') setIntegrationsExpanded(false);
  }, []);

  // Wrapper für setActiveTab der auch die URL aktualisiert
  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    // URL aktualisieren ohne Navigation
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });

    if (EMAIL_TABS.includes(tab)) {
      openNavSection('email');
    } else if (AGENTS_FLOWS_TABS.includes(tab)) {
      openNavSection('agentsFlows');
    } else if (SAFETY_TAB_IDS.includes(tab)) {
      openNavSection(null);
    } else if (ADMIN_TABS.includes(tab) || INTEGRATION_TABS.includes(tab)) {
      openNavSection('admin');
      if (INTEGRATION_TABS.includes(tab)) setIntegrationsExpanded(true);
    } else {
      openNavSection(null);
    }
  }, [searchParams, setSearchParams, openNavSection]);
  
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });

  const [emailSettings, setEmailSettings] = useState({
    imap_host: "imap.hostinger.com",
    imap_port: "993",
    email_username: "",
    email_password: "",
    encryption_type: "SSL",
    subject_line: "",
    smtp_host: "smtp.hostinger.com",
    smtp_port: "465",
    smtp_encryption: "true",
    footer_text: "",
    footer_image_url: "",
    email_queue_duration: "10",
    footer_font: "Helvetica Neue",
    footer_image_position: "below",
    footer_image_size: 80,
    user_name: "Sender Name",
    socialIcons: [
      { iconUrl: "", link: "", size: 32 },
      { iconUrl: "", link: "", size: 32 },
      { iconUrl: "", link: "", size: 32 },
      { iconUrl: "", link: "", size: 32 }
    ],
    socialIconsPosition: "left",
    socialIconsMarginTop: 15,
    socialIconsMarginBottom: 10,
    optOutEnabled: false,
    optOutText: "",
    // Multi-Footer System
    useMultipleFooters: false,
    footerList: []
  });
  
  // State für Multi-Footer-Verwaltung
  const [footerList, setFooterList] = useState([]);
  const [selectedFooterId, setSelectedFooterId] = useState(null);
  const [showFooterMigration, setShowFooterMigration] = useState(false);
  const [showSocialIcons, setShowSocialIcons] = useState(false);
  // 2-Step Footer View: 'overview' = cards grid, 'edit' = master detail view
  const [footerViewMode, setFooterViewMode] = useState('overview');
  const [editingFooterId, setEditingFooterId] = useState(null);
  const [aiSettings, setAiSettings] = useState({
    model: "gpt-4o-mini",
    max_tokens: 250,
    language: "English",
    cleanupGuidelines: `- Remove any redundant or repetitive phrases
- Ensure the text flows naturally and conversationally
- Fix any grammatical errors while maintaining a casual tone
- Remove any AI-like phrasing or overly formal language
- Make sure the email sounds like it was written by a real person
- Eliminate unnecessary pleasantries and corporate jargon
- Keep sentences short and direct
- Remove any closing phrases or signatures that might have been added`,
    replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`,
    replyAgentTemperature: 0.7,
    autoBlockNegativeReplies: false,
    autoBlockMaxScore: 2,
    bounceReduction: false,
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Neuer State für den HTML-Editor
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const editorRef = useRef(null);
  const footerEditorSelectionRef = useRef(null);
  const footerColorInputRef = useRef(null);

  // Neue State-Variablen für die Danger Zone
  const [imports, setImports] = useState([]);
  const [importMetadata, setImportMetadata] = useState([]); // Vollständige Import-History mit Metadaten
  const [selectedImport, setSelectedImport] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [importCounts, setImportCounts] = useState({});
  // Orphan-Scanner States
  const [orphanedImports, setOrphanedImports] = useState([]); // [{name, count, type}]
  const [isScanningOrphans, setIsScanningOrphans] = useState(false);
  const [orphanScanDone, setOrphanScanDone] = useState(false);
  const [orphanScanSummary, setOrphanScanSummary] = useState(null); // {totalDbCount, knownTotal, orphanTotal}
  const [isDeletingOrphan, setIsDeletingOrphan] = useState(false);
  const [orphanDeleteProgress, setOrphanDeleteProgress] = useState('');
  
  // State für Email Scheduling Reset
  const [isResettingScheduling, setIsResettingScheduling] = useState(false);
  const [schedulingResetSuccess, setSchedulingResetSuccess] = useState(false);
  const [schedulingStatus, setSchedulingStatus] = useState({
    lastEmailSent: null,
    lastReplySent: null,
    nextAvailableTime: null,
    isLoading: true
  });
  
  // State für ausklappbare Info-Bereiche
  const [expandedSections, setExpandedSections] = useState({});
  
  // Smart Inbox: kein Schalter mehr, nur noch der vom Backend aufgelöste Zustand
  // (/World/{uid}/settings/inboxStatus) plus die Anzahl verbundener Mail-Konten.
  const [inboxStatus, setInboxStatus] = useState(null);
  const [inboxAccountCount, setInboxAccountCount] = useState(null);

  // State für Turbo AI Setup Modal
  const [showTurboSetup, setShowTurboSetup] = useState(false);

  // Outreach Jobs feature flag
  const [hasOutreachJobs, setHasOutreachJobs] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const flagRef = ref(database, `Users/${userId}/hasOutreachJobs`);
    const unsub = onValue(flagRef, (snap) => {
      setHasOutreachJobs(snap.val() === true);
    });
    return () => unsub();
  }, [userId]);

  
  // New state for expandable AI guidelines
  const [expandedAiGuideline, setExpandedAiGuideline] = useState(null);

  // Funktion zum Umschalten des Ausklappstatus eines Containers
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // userId kommt jetzt aus useActiveWorld() - kein separater Auth-Listener nötig

  // Event-Listener für das Öffnen bestimmter Tabs
  useEffect(() => {
    // Event-Handler-Funktion
    const handleOpenSettingsTab = (event) => {
      if (event.detail && event.detail.tab) {
        setActiveTab(event.detail.tab);
      }
    };

    // Event-Listener hinzufügen
    document.addEventListener('openSettingsTab', handleOpenSettingsTab);

    // Überprüfen, ob das Tab in der Session gespeichert ist
    const savedTab = sessionStorage.getItem('openSettingsTab');
    if (savedTab) {
      setActiveTab(savedTab);
      sessionStorage.removeItem('openSettingsTab');
    }

    // Cleanup-Funktion
    return () => {
      document.removeEventListener('openSettingsTab', handleOpenSettingsTab);
    };
  }, []);

  // Lade vorhandene Einstellungen aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    // Email-Einstellungen laden
    const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
    const unsubscribeEmailListener = onValue(
      emailSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setEmailSettings(snapshot.val());
        }
        
        // Jetzt auch die Footer-Einstellungen laden
        const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
        onValue(footerSettingsRef, (snapshot) => {
          if (snapshot.exists()) {
            const footerData = snapshot.val();
            
            // Prüfe ob Multi-Footer-System aktiv ist
            const useMultipleFooters = footerData.useMultipleFooters === true;
            const footerList = Array.isArray(footerData.footerList) ? footerData.footerList : [];
            
            if (useMultipleFooters && footerList.length > 0) {
              // Multi-Footer-System: Lade footerList
              setFooterList(footerList);
              setEmailSettings(prev => ({
                ...prev,
                useMultipleFooters: true,
                footerList: footerList
              }));
              
              // Setze ersten Footer als ausgewählt, falls vorhanden
              if (footerList.length > 0 && !selectedFooterId) {
                setSelectedFooterId(footerList[0].id);
              }
            } else {
              // Legacy Single-Footer-System: Lade alte Einstellungen
              setEmailSettings(prev => ({
                ...prev,
                footer_text: footerData.footer_text || "",
                footer_image_url: footerData.footer_image_url || "",
                footer_image_position: footerData.footer_image_position || "below",
                footer_font: footerData.footer_font || "Helvetica Neue",
                footer_image_size: footerData.footer_image_size || 80,
                socialIcons: footerData.socialIcons || [
                  { iconUrl: "", link: "", size: 32 },
                  { iconUrl: "", link: "", size: 32 },
                  { iconUrl: "", link: "", size: 32 },
                  { iconUrl: "", link: "", size: 32 }
                ],
                socialIconsPosition: footerData.socialIconsPosition || "left",
                socialIconsMarginTop: footerData.socialIconsMarginTop !== undefined ? footerData.socialIconsMarginTop : 15,
                socialIconsMarginBottom: footerData.socialIconsMarginBottom !== undefined ? footerData.socialIconsMarginBottom : 10,
                optOutEnabled: footerData.optOutEnabled || false,
                optOutText: footerData.optOutText || "",
                useMultipleFooters: false,
                footerList: []
              }));
              setFooterList([]);
            }
          }
          setLoading(false);
        });
      },
      (error) => {
        console.error("Fehler beim Laden der Email-Einstellungen", error);
        setLoading(false);
      }
    );
    
    // AI-Einstellungen laden
    const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
    const unsubscribeAiListener = onValue(
      aiSettingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const loaded = snapshot.val();
          // Backfill defaults for fields added later so existing users see them.
          setAiSettings({
            autoBlockNegativeReplies: false,
            autoBlockMaxScore: 2,
            bounceReduction: false,
            ...loaded,
          });
        } else {
          // Wenn keine AI-Einstellungen existieren, setze die Standardwerte
          setAiSettings({
            model: "gpt-4o-mini",
            max_tokens: 250,
            language: "English",
            cleanupGuidelines: `- Remove any redundant or repetitive phrases
- Ensure the text flows naturally and conversationally
- Fix any grammatical errors while maintaining a casual tone
- Remove any AI-like phrasing or overly formal language
- Make sure the email sounds like it was written by a real person
- Eliminate unnecessary pleasantries and corporate jargon
- Keep sentences short and direct
- Remove any closing phrases or signatures that might have been added`,
            replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`,
            replyAgentTemperature: 0.5,
            autoBlockNegativeReplies: false,
            autoBlockMaxScore: 2,
          });
        }
      },
      (error) => {
        console.error("Fehler beim Laden der AI-Einstellungen", error);
      }
    );
    
    // Auto-Process Emails Einstellung laden
    const autoProcessRef = ref(database, `/World/${userId}/settings/autoProcessEmails`);
    const unsubscribeAutoProcessListener = onValue(
      autoProcessRef,
      (snapshot) => {
        const autoProcessValue = snapshot.exists() ? snapshot.val() : false;
        setAiSettings(prev => ({
          ...prev,
          autoProcessEmails: autoProcessValue
        }));
      },
      (error) => {
        console.error("Fehler beim Laden der autoProcessEmails-Einstellung", error);
      }
    );
    
    // Smart Inbox Status laden (wird ausschließlich vom Backend geschrieben)
    const inboxStatusRef = ref(database, `/World/${userId}/settings/inboxStatus`);
    const unsubscribeInboxStatusListener = onValue(
      inboxStatusRef,
      (snapshot) => {
        setInboxStatus(snapshot.exists() ? snapshot.val() : null);
      },
      (error) => {
        console.error("Fehler beim Laden des Inbox-Status", error);
      }
    );

    return () => {
      unsubscribeEmailListener();
      unsubscribeAiListener();
      unsubscribeAutoProcessListener();
      unsubscribeInboxStatusListener();
    };
  }, [userId]);

  // Die Inbox läuft, sobald ein Mail-Konto verbunden ist — die Kontoanzahl ist
  // daher Teil der Statusanzeige und muss ohne Reload stimmen.
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = onSnapshot(
      collection(firestore, 'world_users', userId, 'emailAccounts'),
      (snapshot) => setInboxAccountCount(snapshot.size),
      (error) => console.error("Fehler beim Zählen der E-Mail-Konten", error)
    );

    return () => unsubscribe();
  }, [userId]);

  // Lade verfügbare Imports aus der Datenbank (Firestore)
  useEffect(() => {
    if (!userId) return;

    const fetchImportsAndCounts = async () => {
      try {
        // 1. Hole die Import-Namen aus der `import_names` Sammlung (inkl. Metadaten)
        const importsCollectionRef = collection(firestore, `World/${userId}/import_names`);
        const importsSnapshot = await getDocs(importsCollectionRef);
        
        // Vollständige Metadaten für Import-History
        const allMetadata = importsSnapshot.docs.map(d => ({
          id: d.id,
          name: d.data().name || d.id,
          last_imported: d.data().last_imported || null,
          imported_count: d.data().imported_count ?? d.data().total_count ?? null,
          source: d.data().source || 'unknown',
          status: d.data().status || 'complete'
        }));

        // Sortiere nach Datum (neueste zuerst) für die History
        const sortedMetadata = [...allMetadata].sort((a, b) => {
          if (!a.last_imported) return 1;
          if (!b.last_imported) return -1;
          return new Date(b.last_imported) - new Date(a.last_imported);
        });
        setImportMetadata(sortedMetadata);

        const importsList = allMetadata.map(m => m.id).sort();
        setImports(importsList);

        // 2. OPTIMIERT: Zähle die Einträge für jeden Import mit getCountFromServer
        const counts = {};
        
        // Initialisiere die Zähler für alle Imports mit 0
        importsList.forEach(name => {
          counts[name] = 0;
        });

        // Verwende getCountFromServer für effizientes Zählen ohne alle Docs zu laden
        const dbCollectionRef = collection(firestore, `World/${userId}/db`);
        
        // Zähle für jeden Import separat (viel effizienter!)
        for (const importName of importsList) {
          try {
            const importQuery = query(dbCollectionRef, where("import_name", "==", importName));
            const countSnapshot = await getCountFromServer(importQuery);
            counts[importName] = countSnapshot.data().count;
          } catch (countError) {
            console.warn(`Fehler beim Zählen von Import ${importName}:`, countError);
            counts[importName] = 0; // Fallback auf 0
          }
        }

        setImportCounts(counts);
        console.log('✅ Import counts loaded efficiently:', counts);
      } catch (error) {
        console.error("Fehler beim Laden der Imports aus Firestore:", error);
        setImports([]);
        setImportMetadata([]);
        setImportCounts({});
      }
    };

    fetchImportsAndCounts();
  }, [userId]);

  // Lade Email Scheduling Status
  useEffect(() => {
    if (!userId) return;
    
    // Listener für lastEmailSent
    const lastEmailSentRef = ref(database, `/World/${userId}/lastEmailSent`);
    const unsubEmail = onValue(lastEmailSentRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Finde den neuesten Zeitstempel
        let latestTimestamp = 0;
        let latestSender = null;
        let scheduledCount = 0;
        
        Object.entries(data).forEach(([key, value]) => {
          if (value.timestamp && value.timestamp > latestTimestamp) {
            latestTimestamp = value.timestamp;
            latestSender = value.senderEmail || key.replace(/_/g, '.');
          }
          if (value.scheduled === true) {
            scheduledCount++;
          }
        });
        
        setSchedulingStatus(prev => ({
          ...prev,
          lastEmailSent: latestTimestamp > 0 ? {
            timestamp: latestTimestamp,
            sender: latestSender,
            scheduledCount
          } : null,
          isLoading: false
        }));
      } else {
        setSchedulingStatus(prev => ({
          ...prev,
          lastEmailSent: null,
          isLoading: false
        }));
      }
    });
    
    // Listener für lastReplySent
    const lastReplySentRef = ref(database, `/World/${userId}/lastReplySent`);
    const unsubReply = onValue(lastReplySentRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        let latestTimestamp = 0;
        let latestSender = null;
        
        Object.entries(data).forEach(([key, value]) => {
          if (value.timestamp && value.timestamp > latestTimestamp) {
            latestTimestamp = value.timestamp;
            latestSender = value.senderEmail || key.replace(/_/g, '.');
          }
        });
        
        setSchedulingStatus(prev => ({
          ...prev,
          lastReplySent: latestTimestamp > 0 ? {
            timestamp: latestTimestamp,
            sender: latestSender
          } : null
        }));
      } else {
        setSchedulingStatus(prev => ({
          ...prev,
          lastReplySent: null
        }));
      }
    });
    
    return () => {
      unsubEmail();
      unsubReply();
    };
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAiChange = (e) => {
    const { name, value } = e.target;
    setAiSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleAiGuidelineExpansion = (guidelineType) => {
    setExpandedAiGuideline(prev => prev === guidelineType ? null : guidelineType);
  };

  const getCharacterCount = (text) => {
    return text ? text.length : 0;
  };

  // Speichere nur Footer-Settings
  const handleSaveFooterSettings = async () => {
    if (!userId) {
      setMessage("Kein Benutzer angemeldet.");
      return;
    }
    
    try {
      setIsSaved(true);
      
      // Speichere Footer-Einstellungen separat unter dem richtigen Pfad
      const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
      
      let footerSettings;
      if (emailSettings.useMultipleFooters === true && footerList.length > 0) {
        // Multi-Footer-System: Speichere footerList
        footerSettings = {
          useMultipleFooters: true,
          footerList: footerList,
          footer_text: emailSettings.footer_text || "",
          footer_image_url: emailSettings.footer_image_url || "",
          footer_image_position: emailSettings.footer_image_position || "below",
          footer_font: emailSettings.footer_font || "Helvetica Neue",
          footer_image_size: emailSettings.footer_image_size || 80,
          socialIcons: emailSettings.socialIcons || [
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 }
          ],
          socialIconsPosition: emailSettings.socialIconsPosition || "left",
          socialIconsMarginTop: emailSettings.socialIconsMarginTop !== undefined ? emailSettings.socialIconsMarginTop : 15,
          socialIconsMarginBottom: emailSettings.socialIconsMarginBottom !== undefined ? emailSettings.socialIconsMarginBottom : 10,
          optOutEnabled: emailSettings.optOutEnabled || false,
          optOutText: emailSettings.optOutText || ""
        };
      } else {
        // Legacy Single-Footer-System
        footerSettings = {
          useMultipleFooters: false,
          footerList: [],
          footer_text: emailSettings.footer_text || "",
          footer_image_url: emailSettings.footer_image_url || "",
          footer_image_position: emailSettings.footer_image_position || "below",
          footer_font: emailSettings.footer_font || "Helvetica Neue",
          footer_image_size: emailSettings.footer_image_size || 80,
          socialIcons: emailSettings.socialIcons || [
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 },
            { iconUrl: "", link: "", size: 32 }
          ],
          socialIconsPosition: emailSettings.socialIconsPosition || "left",
          socialIconsMarginTop: emailSettings.socialIconsMarginTop !== undefined ? emailSettings.socialIconsMarginTop : 15,
          socialIconsMarginBottom: emailSettings.socialIconsMarginBottom !== undefined ? emailSettings.socialIconsMarginBottom : 10,
          optOutEnabled: emailSettings.optOutEnabled || false,
          optOutText: emailSettings.optOutText || ""
        };
      }
      
      await update(footerSettingsRef, footerSettings);
      
      console.log("Footer-Einstellungen erfolgreich gespeichert:", footerSettings);
      setMessage("Footer settings saved successfully.");
      
      setTimeout(() => {
        setIsSaved(false);
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Speichern der Footer-Einstellungen:", error);
      setMessage("Error saving footer settings: " + error.message);
      setTimeout(() => {
        setMessage("");
      }, 3000);
      setIsSaved(false);
    }
  };

  const handleSave = async () => {
    if (!userId) {
      setMessage("Kein Benutzer angemeldet.");
      return;
    }
    
    
    try {
      // Setze den Button-Status auf "gespeichert"
      setIsSaved(true);
      
      // Speichere die entsprechenden Einstellungen je nach aktivem Tab
      if (activeTab === 'ai') {
        const aiSettingsRef = ref(database, `/World/${userId}/settings/ai`);
        await update(aiSettingsRef, aiSettings);
        
        // Speichere auch die autoProcessEmails Einstellung separat
        const autoProcessRef = ref(database, `/World/${userId}/settings/autoProcessEmails`);
        await set(autoProcessRef, aiSettings.autoProcessEmails || false);
        
        console.log("AI-Einstellungen erfolgreich gespeichert:", aiSettings);
        setMessage("AI settings saved successfully.");
      } else if (activeTab === 'emailPreferences') {
        // Logge die zu speichernden Einstellungen
        console.log("Speichere E-Mail-Einstellungen:", emailSettings);
        
        // Verschlüssele das Passwort, falls vorhanden und noch nicht verschlüsselt
        let settingsToSave = { ...emailSettings };
        if (emailSettings.email_password && !isPasswordEncrypted(emailSettings.email_password)) {
          console.log('🔐 Encrypting email password before saving...');
          settingsToSave.email_password = await encryptEmailPassword(emailSettings.email_password);
          console.log('✅ Email password encrypted successfully');
        } else if (emailSettings.email_password) {
          console.log('ℹ️ Email password is already encrypted');
        }
        
        // Speichere allgemeine E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        await update(emailSettingsRef, settingsToSave);
        
        // Speichere Footer-Einstellungen separat unter dem richtigen Pfad
        const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
        
        let footerSettings;
        if (emailSettings.useMultipleFooters === true && footerList.length > 0) {
          // Multi-Footer-System: Speichere footerList
          footerSettings = {
            useMultipleFooters: true,
            footerList: footerList,
            footer_text: emailSettings.footer_text || "",
            footer_image_url: emailSettings.footer_image_url || "",
            footer_image_position: emailSettings.footer_image_position || "below",
            footer_font: emailSettings.footer_font || "Helvetica Neue",
            footer_image_size: emailSettings.footer_image_size || 80,
            socialIcons: emailSettings.socialIcons || [
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 }
            ],
            socialIconsPosition: emailSettings.socialIconsPosition || "left",
            socialIconsMarginTop: emailSettings.socialIconsMarginTop !== undefined ? emailSettings.socialIconsMarginTop : 15,
            socialIconsMarginBottom: emailSettings.socialIconsMarginBottom !== undefined ? emailSettings.socialIconsMarginBottom : 10,
            optOutEnabled: emailSettings.optOutEnabled || false,
            optOutText: emailSettings.optOutText || ""
          };
        } else {
          // Legacy Single-Footer-System
          footerSettings = {
            useMultipleFooters: false,
            footerList: [],
            footer_text: emailSettings.footer_text || "",
            footer_image_url: emailSettings.footer_image_url || "",
            footer_image_position: emailSettings.footer_image_position || "below",
            footer_font: emailSettings.footer_font || "Helvetica Neue",
            footer_image_size: emailSettings.footer_image_size || 80,
            socialIcons: emailSettings.socialIcons || [
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 }
            ],
            socialIconsPosition: emailSettings.socialIconsPosition || "left",
            socialIconsMarginTop: emailSettings.socialIconsMarginTop !== undefined ? emailSettings.socialIconsMarginTop : 15,
            socialIconsMarginBottom: emailSettings.socialIconsMarginBottom !== undefined ? emailSettings.socialIconsMarginBottom : 10,
            optOutEnabled: emailSettings.optOutEnabled || false,
            optOutText: emailSettings.optOutText || ""
          };
        }
        
        await update(footerSettingsRef, footerSettings);
        
        console.log("E-Mail-Einstellungen erfolgreich gespeichert:", settingsToSave);
        console.log("Footer-Einstellungen erfolgreich gespeichert:", footerSettings);
        setMessage("Email settings saved successfully.");
      } else {
        // Logge die zu speichernden Einstellungen
        console.log("Save email settings:", emailSettings);
        
        // Verschlüssele das Passwort, falls vorhanden und noch nicht verschlüsselt
        let settingsToSave = { ...emailSettings };
        if (emailSettings.email_password && !isPasswordEncrypted(emailSettings.email_password)) {
          console.log('🔐 Encrypting email password before saving...');
          settingsToSave.email_password = await encryptEmailPassword(emailSettings.email_password);
          console.log('✅ Email password encrypted successfully');
        } else if (emailSettings.email_password) {
          console.log('ℹ️ Email password is already encrypted');
        }
        
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        await update(emailSettingsRef, settingsToSave);
        
        console.log("Allgemeine Einstellungen erfolgreich gespeichert:", settingsToSave);
        setMessage("Settings saved successfully.");
      }

      // Setze den Button und die Message nach 3 Sekunden zurück
      setTimeout(() => {
        setIsSaved(false);
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error("Fehler beim Speichern der Einstellungen:", error);
      setMessage("Error saving settings: " + error.message);
      // Bei einem Fehler auch nach 3 Sekunden die Fehlermeldung ausblenden
      setTimeout(() => {
        setMessage("");
      }, 3000);
      setIsSaved(false);
    }
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = e.dataTransfer?.files || [];
    if (droppedFiles.length > 0) {
      await handleImageUpload({ target: { files: [droppedFiles[0]] } });
    }
  };

  const handleImageUpload = async (e) => {
    const rawFile = e.target.files[0];
    if (!rawFile) return;
    
    // Überprüfe Dateityp – SVG bewusst NICHT erlaubt: Outlook/Gmail blocken SVG
    // in E-Mails und es erhöht den Spam-Score massiv.
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(rawFile.type)) {
      setMessage("Only JPG, PNG and GIF files are allowed (SVG is blocked by most email clients).");
      return;
    }

    // Obergrenze für die Originaldatei, bevor wir sie im Browser dekodieren
    // (Schutz gegen versehentlich riesige Dateien). Die Kompression darunter
    // bringt das Ergebnis dann auf ≤ 200 KB.
    if (rawFile.size > 10 * 1024 * 1024) {
      setMessage("The file is too large. Please use an image under 10 MB.");
      return;
    }
    
    try {
      setUploading(true);
      setMessage("Optimizing image...");

      // Automatisch komprimieren/verkleinern, dann hart bei 200 KB kappen.
      const file = await compressFooterImage(rawFile);
      if (file.size > FOOTER_IMAGE_MAX_BYTES) {
        setMessage("Image is still larger than 200 KB after compression. Please use a smaller logo.");
        setUploading(false);
        return;
      }

      setMessage("Picture is being uploaded...");
      
      // Erstelle einen eindeutigen Dateinamen
      const fileName = `footer_images/${userId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      console.log("Speichere Bild unter:", fileName);
      
      const storage = getStorage();
      const fileRef = storageRef(storage, fileName);
      
      // Lade die Datei hoch
      await uploadBytes(fileRef, file);
      console.log("Picture uploaded successfully");
      
      // Hole die Download-URL
      const downloadURL = await getDownloadURL(fileRef);
      console.log("Picture URL:", downloadURL);
      
      // Aktualisiere die Einstellungen
      setEmailSettings(prev => {
        const updated = {
          ...prev,
          footer_image_url: downloadURL
        };
        console.log("Updated email settings:", updated);
        return updated;
      });
      
      setMessage("Picture uploaded successfully! Click 'Save Settings' to save the changes.");
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage("Error uploading image: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handler für Footer-Image-Upload im Multi-Footer-System
  const handleFooterImageUpload = async (rawFile, footerId) => {
    if (!rawFile) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(rawFile.type)) {
      setMessage("Only JPG, PNG and GIF files are allowed (SVG is blocked by most email clients).");
      return;
    }

    // Obergrenze für die Originaldatei, bevor wir sie im Browser dekodieren.
    if (rawFile.size > 10 * 1024 * 1024) {
      setMessage("The file is too large. Please use an image under 10 MB.");
      return;
    }
    
    try {
      setUploading(true);
      setMessage("Optimizing image...");

      // Automatisch komprimieren/verkleinern, dann hart bei 200 KB kappen.
      const file = await compressFooterImage(rawFile);
      if (file.size > FOOTER_IMAGE_MAX_BYTES) {
        setMessage("Image is still larger than 200 KB after compression. Please use a smaller logo.");
        setUploading(false);
        return;
      }

      setMessage("Picture is being uploaded...");
      
      const fileName = `footer_images/${userId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storage = getStorage();
      const fileRef = storageRef(storage, fileName);
      
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      handleUpdateFooter(footerId, { footer_image_url: downloadURL });
      setMessage("Picture uploaded successfully! Click 'Save Settings' to save the changes.");
    } catch (error) {
      console.error("Error uploading image:", error);
      setMessage("Error uploading image: " + error.message);
    } finally {
      setUploading(false);
    }
  };
  
  // Handler für Social-Icon-Upload im Multi-Footer-System
  const handleFooterSocialIconUpload = async (file, footerId, iconIndex) => {
    if (!file) return;
    
    if (file.type !== 'image/svg+xml') {
      setMessage("Only SVG files are allowed for social icons.");
      return;
    }
    
    if (file.size > 500 * 1024) {
      setMessage("The icon file is too large. Maximum size: 500KB");
      return;
    }
    
    try {
      setUploading(true);
      setMessage("Icon is being uploaded...");
      
      const fileName = `social_icons/${userId}/${Date.now()}_icon_${iconIndex}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storage = getStorage();
      const fileRef = storageRef(storage, fileName);
      
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      
      const currentFooter = footerList.find(f => f.id === footerId);
      if (currentFooter) {
        const updatedIcons = [...(currentFooter.socialIcons || [])];
        updatedIcons[iconIndex] = {
          ...updatedIcons[iconIndex],
          iconUrl: downloadURL
        };
        handleUpdateFooter(footerId, { socialIcons: updatedIcons });
      }
      
      setMessage("Icon uploaded successfully! Click 'Save Settings' to save the changes.");
    } catch (error) {
      console.error("Error uploading icon:", error);
      setMessage("Error uploading icon: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSocialIconUpload = async (e, iconIndex) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Überprüfe Dateityp - nur SVG
    if (file.type !== 'image/svg+xml') {
      setMessage("Only SVG files are allowed for social icons.");
      return;
    }
    
    // Überprüfe Dateigröße (max 500KB für Icons)
    if (file.size > 500 * 1024) {
      setMessage("The icon file is too large. Maximum size: 500KB");
      return;
    }
    
    try {
      setUploading(true);
      setMessage("Icon is being uploaded...");
      
      // Erstelle einen eindeutigen Dateinamen
      const fileName = `social_icons/${userId}/${Date.now()}_icon_${iconIndex}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      console.log("Speichere Icon unter:", fileName);
      
      const storage = getStorage();
      const fileRef = storageRef(storage, fileName);
      
      // Lade die Datei hoch
      await uploadBytes(fileRef, file);
      console.log("Icon uploaded successfully");
      
      // Hole die Download-URL
      const downloadURL = await getDownloadURL(fileRef);
      console.log("Icon URL:", downloadURL);
      
      // Aktualisiere die Einstellungen
      setEmailSettings(prev => {
        const updatedIcons = [...prev.socialIcons];
        updatedIcons[iconIndex] = {
          ...updatedIcons[iconIndex],
          iconUrl: downloadURL
        };
        return {
          ...prev,
          socialIcons: updatedIcons
        };
      });
      
      setMessage("Icon uploaded successfully! Click 'Save Settings' to save the changes.");
    } catch (error) {
      console.error("Error uploading icon:", error);
      setMessage("Error uploading icon: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Migration: Konvertiert Legacy Single-Footer zu Multi-Footer-System
  const handleMigrateToMultiFooter = async () => {
    if (!userId) {
      setMessage("Kein Benutzer angemeldet.");
      return;
    }
    
    try {
      // Erstelle einen Footer aus den aktuellen Legacy-Einstellungen
      const migratedFooter = {
        id: `footer_${Date.now()}`,
        name: "Migrated Footer",
        footer_text: emailSettings.footer_text || "",
        footer_image_url: emailSettings.footer_image_url || "",
        footer_image_position: emailSettings.footer_image_position || "below",
        footer_font: emailSettings.footer_font || "Helvetica Neue",
        footer_image_size: emailSettings.footer_image_size || 80,
        socialIcons: emailSettings.socialIcons || [
          { iconUrl: "", link: "", size: 32 },
          { iconUrl: "", link: "", size: 32 },
          { iconUrl: "", link: "", size: 32 },
          { iconUrl: "", link: "", size: 32 }
        ],
        socialIconsPosition: emailSettings.socialIconsPosition || "left",
        socialIconsMarginTop: emailSettings.socialIconsMarginTop !== undefined ? emailSettings.socialIconsMarginTop : 15,
        socialIconsMarginBottom: emailSettings.socialIconsMarginBottom !== undefined ? emailSettings.socialIconsMarginBottom : 10,
        optOutEnabled: emailSettings.optOutEnabled || false,
        optOutText: emailSettings.optOutText || "",
        isActive: true,
        createdAt: Date.now()
      };
      
      // Setze Multi-Footer-System aktiv
      const newFooterList = [migratedFooter];
      setFooterList(newFooterList);
      setSelectedFooterId(migratedFooter.id);
      
      // Aktualisiere emailSettings
      setEmailSettings(prev => ({
        ...prev,
        useMultipleFooters: true,
        footerList: newFooterList
      }));
      
      // Speichere in Firebase
      const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
      await update(footerSettingsRef, {
        useMultipleFooters: true,
        footerList: newFooterList
      });
      
      setMessage("Successfully migrated to multi-footer system! You can now add more footers.");
      setTimeout(() => {
        setMessage("");
        setShowFooterMigration(false);
      }, 3000);
    } catch (error) {
      console.error("Error migrating to multi-footer system:", error);
      setMessage("Error migrating: " + error.message);
    }
  };
  
  // Handler für Multi-Footer-Verwaltung
  const handleAddFooter = () => {
    const newFooter = {
      id: `footer_${Date.now()}`,
      name: `Footer ${footerList.length + 1}`,
      footer_text: "",
      footer_image_url: "",
      footer_image_position: "below",
      footer_font: "Helvetica Neue",
      footer_image_size: 80,
      socialIcons: [
        { iconUrl: "", link: "", size: 32 },
        { iconUrl: "", link: "", size: 32 },
        { iconUrl: "", link: "", size: 32 },
        { iconUrl: "", link: "", size: 32 }
      ],
      socialIconsPosition: "left",
      socialIconsMarginTop: 15,
      socialIconsMarginBottom: 10,
      optOutEnabled: true,
      optOutText: "Unsubscribe",
      isActive: true,
      createdAt: Date.now()
    };
    
    const updatedList = [...footerList, newFooter];
    setFooterList(updatedList);
    setSelectedFooterId(newFooter.id);
    setEmailSettings(prev => ({
      ...prev,
      footerList: updatedList
    }));
  };
  
  /**
   * Löscht einen Footer und ENTKOPPELT ihn von allen Email-Accounts, die ihn referenzieren.
   *
   * Drei Operationen, die alle persistiert werden MÜSSEN — sonst hinterlassen
   * wir verwaiste `footerId`-Pointer in Firestore-Account-Docs:
   *
   *   1) RTDB:  /World/{uid}/settings/footer/footerList — Footer rauslöschen
   *   2) Firestore (Batch):  world_users/{uid}/emailAccounts/{id}.footerId = ''
   *      für JEDES Konto, das den gelöschten Footer aktuell verlinkt
   *   3) Lokaler State: footerList, emailSettings, selectedFooterId, editingFooterId
   *
   * `emailAccounts` lebt im EmailAccountManager-Child (eigener `onSnapshot`-Listener
   * auf `world_users/{uid}/emailAccounts`), deshalb lesen wir sie hier per
   * one-shot `getDocs` direkt aus Firestore. Sobald wir die Account-Docs
   * geupdated haben, refresht der Child automatisch über seinen Snapshot —
   * keine Cross-Component-State-Synchronisation nötig.
   *
   * Crash-Schutz beim Senden ist BEREITS server-seitig vorhanden
   * (processOutreachJobs.js + processFlowAutomation.js fallen auf den
   * ersten aktiven Footer zurück, wenn `senderDoc.footerId` ins Leere zeigt).
   * Trotzdem räumen wir hier auf, damit kein User-sichtbares Dropdown auf
   * einen Geist-Footer zeigt und die DB konsistent bleibt.
   */
  const handleDeleteFooter = async (footerId) => {
    if (footerList.length <= 1) {
      setMessage("You need at least one footer.");
      return;
    }

    if (!userId) {
      setMessage("Not signed in — cannot delete footer.");
      return;
    }

    const footerToDelete = footerList.find(f => f && f.id === footerId);
    const footerName = footerToDelete?.name || 'this footer';

    // Welche Konten verlinken aktuell genau diesen Footer? Wir lesen
    // `world_users/{uid}/emailAccounts` einmalig direkt, damit der Confirm-
    // Dialog die Anzahl beziffern kann (und der User nicht blind klickt).
    // Side-effect: Firestore one-shot read, ~50-150ms — vertretbar.
    let affectedAccountIds = [];
    try {
      const accountsCollectionRef = collection(firestore, 'world_users', userId, 'emailAccounts');
      const snapshot = await getDocs(accountsCollectionRef);
      affectedAccountIds = snapshot.docs
        .filter(d => d.data()?.footerId === footerId)
        .map(d => d.id);
    } catch (err) {
      console.error('[handleDeleteFooter] account scan failed:', err);
      setMessage(`Could not check linked accounts: ${err?.message || 'unknown error'}`);
      return;
    }

    const affectedCount = affectedAccountIds.length;
    const baseMessage = `Are you sure you want to delete "${footerName}"?`;
    const decouplingMessage = affectedCount > 0
      ? ` ${affectedCount} email account${affectedCount === 1 ? '' : 's'} currently use${affectedCount === 1 ? 's' : ''} this footer and will fall back to the default footer.`
      : '';

    setConfirmModal({
      open: true,
      title: "Delete Footer",
      message: `${baseMessage}${decouplingMessage}`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));

        const updatedList = footerList.filter(f => f && f.id !== footerId);

        try {
          // Schritt 1: RTDB-footerList neu schreiben. Wir nehmen `update` über
          // den Container-Pfad mit `{ footerList: updatedList }`, damit die
          // ARRAY-Form erhalten bleibt (sonst macht Firebase ein Object aus
          // den Indices und der Reader, der `Array.isArray()` checkt, fällt
          // wieder auf den Legacy-Branch zurück).
          await update(
            ref(database, `/World/${userId}/settings/footer`),
            { footerList: updatedList }
          );

          // Schritt 2: Firestore-Batch — alle betroffenen Email-Accounts
          // entkoppeln. Leerer String `''` (statt `null`/`deleteField()`)
          // matcht das bestehende Schema und wird vom Backend gleich behandelt
          // wie "noch nie ein Footer ausgewählt". Der EmailAccountManager-
          // Child sieht die Updates automatisch über seinen `onSnapshot`.
          if (affectedCount > 0) {
            const batch = writeBatch(firestore);
            for (const accountId of affectedAccountIds) {
              const accountDocRef = doc(firestore, 'world_users', userId, 'emailAccounts', accountId);
              batch.update(accountDocRef, { footerId: '' });
            }
            await batch.commit();
          }

          // Schritt 3: Lokaler Footer-State — erst NACH erfolgreicher
          // Persistierung, damit ein Fehler nicht zu einer State/DB-Drift führt.
          setFooterList(updatedList);

          if (selectedFooterId === footerId) {
            setSelectedFooterId(updatedList.length > 0 ? updatedList[0].id : null);
          }
          // Wenn der Edit-Modus gerade auf diesen Footer offen ist, zurück
          // zur Übersicht — sonst würde der Edit-View auf nichts mehr zeigen.
          if (editingFooterId === footerId) {
            setEditingFooterId(null);
            setFooterViewMode('overview');
          }

          setEmailSettings(prev => ({
            ...prev,
            footerList: updatedList
          }));

          setMessage(
            affectedCount > 0
              ? `Footer deleted and unlinked from ${affectedCount} email account${affectedCount === 1 ? '' : 's'}.`
              : "Footer deleted."
          );
        } catch (err) {
          console.error('[handleDeleteFooter] failed:', err);
          setMessage(`Could not delete footer: ${err?.message || 'unknown error'}`);
        }
      }
    });
  };
  
  const handleUpdateFooter = (footerId, updates) => {
    const updatedList = footerList.map(f => 
      f.id === footerId ? { ...f, ...updates } : f
    );
    setFooterList(updatedList);
    setEmailSettings(prev => ({
      ...prev,
      footerList: updatedList
    }));
  };
  
  const getCurrentFooter = () => {
    if (!selectedFooterId) return null;
    return footerList.find(f => f.id === selectedFooterId);
  };
  
  // Selection im Footer-Editor speichern (vor Fokusverlust durch Toolbar-Klick)
  const saveFooterEditorSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !editorRef.current) return;
    if (!editorRef.current.contains(sel.anchorNode)) return;
    try {
      footerEditorSelectionRef.current = sel.getRangeAt(0).cloneRange();
    } catch (_) {
      footerEditorSelectionRef.current = null;
    }
  };

  // Selection bei jeder Änderung im Editor speichern, damit Color-Picker/Select sie später noch haben
  useEffect(() => {
    const onSelectionChange = () => {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (!editor || !sel || sel.rangeCount === 0) return;
      const node = sel.anchorNode;
      if (!node || !editor.contains(node)) return;
      try {
        footerEditorSelectionRef.current = sel.getRangeAt(0).cloneRange();
      } catch (_) {
        footerEditorSelectionRef.current = null;
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  // Selection wiederherstellen und Formatierung anwenden, damit Farbe/Größe etc. nicht deselektieren
  const formatText = (command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    const sel = window.getSelection();
    if (footerEditorSelectionRef.current) {
      try {
        sel.removeAllRanges();
        sel.addRange(footerEditorSelectionRef.current);
      } catch (_) {}
      footerEditorSelectionRef.current = null;
    }

    document.execCommand(command, false, value);

    if (editor) {
      const newHtml = editor.innerHTML;
      if (emailSettings.useMultipleFooters === true && editingFooterId) {
        handleUpdateFooter(editingFooterId, { footer_text: newHtml });
      } else {
        setEmailSettings(prev => ({
          ...prev,
          footer_text: newHtml
        }));
      }
    }
  };

  // E-Mail Plan Konfiguration (Limits basierend auf 840 aktive Minuten/Tag)
  // Daily send limits and account caps come from the plan config so the slider
  // ceilings here always match what the backend actually grants.
  const EMAIL_PLANS = {
    standard: { label: 'Standard', limit: EMAIL_PLAN_LIMITS.standard, color: '#6b7280' },
    premium: { label: 'Premium', limit: EMAIL_PLAN_LIMITS.premium, color: '#3b82f6' },
    unlimited: { label: 'Unlimited', limit: EMAIL_PLAN_LIMITS.unlimited, color: '#10b981' },
  };

  const EMAIL_ACCOUNT_LIMITS = Object.values(CREDIT_PLANS).reduce((acc, plan) => {
    acc[plan.emailPlan] = plan.features.emailAccounts;
    return acc;
  }, {});

  const EmailAccountManager = ({ userId, footerList = [], useMultipleFooters = false }) => {
    const [emailAccounts, setEmailAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [showAddAccount, setShowAddAccount] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [selectedAccountLogs, setSelectedAccountLogs] = useState(null);
    const [emailLogs, setEmailLogs] = useState({});
    const [openKebabId, setOpenKebabId] = useState(null);
    const [showHealthModal, setShowHealthModal] = useState(false);
    const [healthAccount, setHealthAccount] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [healthResult, setHealthResult] = useState(null);
    const [connectingMicrosoft, setConnectingMicrosoft] = useState(false);
    const [isSavingAccount, setIsSavingAccount] = useState(false);
    const [newAccount, setNewAccount] = useState({
      email: '',
      password: '',
      imap_host: 'imap.hostinger.com',
      imap_port: '993',
      encryption_type: 'SSL',
      smtp_host: 'smtp.hostinger.com',
      smtp_port: '465',
      smtp_encryption: 'true',
      sender_name: '',
      text_signature: '',
      queue_duration: '32',
      sleep_time_start: '22:00',
      sleep_time_end: '08:00',
      skip_weekends: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    
    // E-Mail Plan States
    const [emailPlan, setEmailPlan] = useState('standard');
    const [dailyEmailLimit, setDailyEmailLimit] = useState(45);
    const [hasOutreachJobs, setHasOutreachJobs] = useState(false);

    const accountLimit = EMAIL_ACCOUNT_LIMITS[emailPlan] || 5;
    const isAccountLimitReached = emailAccounts.length >= accountLimit;

    // Berechne E-Mails pro Tag basierend auf Delay (in Minuten)
    // Annahme: 14h aktive Zeit pro Tag (10h Schlafzeit von 22:00-08:00)
    const calculateEmailsPerDay = (delayMinutes) => {
      const activeHoursPerDay = 14; // Standard sleep time: 22:00-08:00
      const activeMinutesPerDay = activeHoursPerDay * 60;
      return Math.round(activeMinutesPerDay / delayMinutes);
    };

    // Berechne die Gesamt-Kapazität aller Accounts
    const calculateTotalCapacity = (accounts, excludeAccountId = null) => {
      return accounts.reduce((total, account) => {
        if (excludeAccountId && account.id === excludeAccountId) {
          return total;
        }
        const delay = parseInt(account.queue_duration) || 32;
        return total + calculateEmailsPerDay(delay);
      }, 0);
    };

    // Berechne den minimalen Delay für einen Account basierend auf verfügbarer Kapazität
    const calculateMinDelayForAccount = (accountId) => {
      const otherAccountsCapacity = calculateTotalCapacity(emailAccounts, accountId);
      const remainingCapacity = Math.max(0, dailyEmailLimit - otherAccountsCapacity);
      
      if (remainingCapacity <= 0) {
        return 500; // Max delay wenn keine Kapazität übrig
      }
      
      const activeMinutesPerDay = 14 * 60;
      const minDelay = Math.ceil(activeMinutesPerDay / remainingCapacity);
      return Math.max(5, Math.min(500, minDelay)); // Zwischen 5 und 500
    };

    // Berechne den maximalen Emails/Tag für einen Account
    const calculateMaxEmailsForAccount = (accountId) => {
      const otherAccountsCapacity = calculateTotalCapacity(emailAccounts, accountId);
      return Math.max(0, dailyEmailLimit - otherAccountsCapacity);
    };

    useEffect(() => {
      if (!selectedAccount && !showAddAccount) return;
      const close = () => {
        setSelectedAccount(null);
        setShowAddAccount(false);
      };
      const onKey = (e) => { if (e.key === 'Escape') close(); };
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = prevOverflow;
        window.removeEventListener('keydown', onKey);
      };
    }, [selectedAccount, showAddAccount]);

    // Lade User's Email Plan aus Realtime Database
    useEffect(() => {
      if (!userId) return;

      const userRef = ref(database, `Users/${userId}`);
      const unsubscribe = onValue(userRef, (snapshot) => {
        const userData = snapshot.val();
        if (userData) {
          setEmailPlan(userData.emailPlan || 'standard');
          setDailyEmailLimit(userData.dailyEmailLimit || EMAIL_PLANS[userData.emailPlan || 'standard'].limit);
          setHasOutreachJobs(userData.hasOutreachJobs === true);
        }
      }, (error) => {
        console.error("Fehler beim Laden des E-Mail-Plans:", error);
      });

      return () => unsubscribe();
    }, [userId]);

    // Lade alle E-Mail-Konten aus Firestore
    useEffect(() => {
      if (!userId) return;

      const accountsCollectionRef = collection(firestore, 'world_users', userId, 'emailAccounts');
      const unsubscribe = onSnapshot(accountsCollectionRef, (snapshot) => {
        const accounts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmailAccounts(accounts);
        // Lade auch die Logs für alle Accounts
        loadEmailLogsForAccounts(accounts); 
        setIsLoading(false);
      }, (error) => {
        console.error("Fehler beim Laden der E-Mail-Konten aus Firestore:", error);
        setMessage("Fehler beim Laden der E-Mail-Konten.");
        setIsLoading(false);
      });

      return () => unsubscribe();
    }, [userId]);

    // 🔷 MICROSOFT: Prüfe URL-Parameter nach OAuth-Redirect
    useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const error = urlParams.get('error');
      const email = urlParams.get('email');

      if (success === 'microsoft_connected') {
        setMessage(`✓ Microsoft account ${email ? `(${decodeURIComponent(email)})` : ''} connected successfully!`);
        // Parameter aus URL entfernen
        const newUrl = window.location.pathname + '?view=settings&tab=email';
        window.history.replaceState({}, '', newUrl);
      } else if (error) {
        setMessage(`✗ Error connecting Microsoft: ${decodeURIComponent(error)}`);
        // Parameter aus URL entfernen
        const newUrl = window.location.pathname + '?view=settings&tab=email';
        window.history.replaceState({}, '', newUrl);
      }
    }, []);

    // Funktion zum Laden der E-Mail-Logs für alle Accounts aus Firestore
    const loadEmailLogsForAccounts = async (accounts) => {
      if (!userId || !accounts.length) return;
      
      const logsData = {};
      
      for (const account of accounts) {
        try {
          // Lade Logs aus Firestore Collection world_email_logs/{userId}/entries
          const logsCollectionRef = collection(firestore, 'world_email_logs', userId, 'entries');
          const q = query(
            logsCollectionRef, 
            where('senderEmail', '==', account.email),
            orderBy('timestamp', 'desc'),
            limit(50) // Begrenze auf die letzten 50 Logs pro Account
          );
          
          const snapshot = await getDocs(q);
          const logs = [];
          
          snapshot.forEach((doc) => {
            const logData = doc.data();
            logs.push({
              id: doc.id,
              ...logData,
              // Konvertiere Firestore Timestamp zu JavaScript Date für Kompatibilität
              timestamp: logData.timestamp?.toDate ? logData.timestamp.toDate().getTime() : logData.timestamp
            });
          });
          
          console.log(`Loaded ${logs.length} logs for email account ${account.email} from Firestore`);
          logsData[account.email] = logs;
        } catch (error) {
          console.error(`Fehler beim Laden der Logs für ${account.email} aus Firestore:`, error);
          logsData[account.email] = [];
        }
      }
      
      setEmailLogs(logsData);
    };

    // Funktion zum Ermitteln des Log-Status für einen Account
    const getAccountLogStatus = (email) => {
      const logs = emailLogs[email] || [];
      if (logs.length === 0) return 'unknown';
      
      const latestLog = logs[0]; // Neuester Log (bereits sortiert)
      
      if (latestLog.status === 'success') {
        return 'success';
      } else if (latestLog.status === 'error') {
        return 'error';
      } else {
        return 'info';
      }
    };

    // Funktion zum Öffnen des Log-Modals
    const handleShowLogs = (account) => {
      setSelectedAccountLogs(account);
      setShowLogsModal(true);
    };

    // Funktion zum Öffnen des Account-Health-Checks (Domain-Auth + Warmup + Deliverability)
    const handleCheckHealth = async (account) => {
      setHealthAccount(account);
      setHealthResult(null);
      setHealthLoading(true);
      setShowHealthModal(true);

      const email = (account.email || '').trim();
      const domain = email.split('@')[1] || '';
      const sanitizedEmail = email.replace(/[.#$\/\[\]]/g, '_');

      const out = { domain: null, warmup: null, deliverability: {}, warmupConfig: {}, errors: [] };

      // 1) Domain-Authentifizierung (MX / SPF / DKIM / DMARC) — Backend-Callable
      try {
        const checkDomainHealth = httpsCallable(functions, 'checkDomainHealth');
        const res = await checkDomainHealth({ domain });
        out.domain = res.data;
      } catch (err) {
        console.error('Domain health check failed:', err);
        out.errors.push('domain');
      }

      // 2) Warmup-Score (nur aussagekräftig wenn Warmup aktiv)
      try {
        const getWarmupScore = httpsCallable(functions, 'getWarmupScore');
        const res = await getWarmupScore({ email, userId });
        out.warmup = res.data;
      } catch (err) {
        // non-blocking
      }

      // 3) Echte Sende-Health + Warmup-Config (RTDB, clientseitig)
      try {
        const [healthSnap, configSnap] = await Promise.all([
          get(ref(database, `World/${userId}/emailHealth/${sanitizedEmail}`)),
          get(ref(database, `World/${userId}/warmupConfig/${sanitizedEmail}`)),
        ]);
        out.deliverability = healthSnap.exists() ? (healthSnap.val() || {}) : {};
        out.warmupConfig = configSnap.exists() ? (configSnap.val() || {}) : {};
      } catch (err) {
        // non-blocking
      }

      setHealthResult(out);
      setHealthLoading(false);
    };

    // Neue Funktion zum Speichern der Verzögerungsdauer in den E-Mail-Einstellungen
    const updateQueueDurationInEmailSettings = async (email, queueDuration) => {
      try {
        // Sanitize die E-Mail-Adresse für die Verwendung als Schlüssel
        const sanitizedEmail = email.replace(/[.#$\/\[\]]/g, '_');
        const durationKey = `email_queue_duration_${sanitizedEmail}`;
        
        // Referenz zu den E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        
        // Hole die aktuellen Einstellungen
        const snapshot = await get(emailSettingsRef);
        const currentSettings = snapshot.exists() ? snapshot.val() : {};
        
        // Setze die Verzögerungsdauer für diesen Absender
        const updatedSettings = {
          ...currentSettings,
          [durationKey]: queueDuration
        };
        
        // Aktualisiere die Einstellungen
        await set(emailSettingsRef, updatedSettings);
        
        console.log(`Verzögerungsdauer für ${email} auf ${queueDuration} Minuten aktualisiert`);
      } catch (error) {
        console.error('Fehler beim Speichern der Verzögerungsdauer:', error);
        setMessage(`Fehler beim Speichern der Verzögerungsdauer: ${error.message}`);
      }
    };

    const handleAddAccount = async () => {
      if (isAccountLimitReached) {
        setMessage(`Account limit reached. Your plan allows up to ${accountLimit} email accounts. Upgrade to add more.`);
        return;
      }
      if (!newAccount.email || !newAccount.password) {
        setMessage('Bitte gib E-Mail-Adresse und Passwort ein.');
        return;
      }
      if (isSavingAccount) return;
      
      try {
        setIsSavingAccount(true);
        setMessage('Speichere E-Mail-Konto...');
        
        let passwordToSave = newAccount.password;
        if (!isPasswordEncrypted(newAccount.password)) {
          console.log('🔐 Encrypting password before saving...');
          passwordToSave = await encryptEmailPassword(newAccount.password);
          console.log('✅ Password encrypted successfully');
        }

        const accountToSave = {
          ...newAccount,
          email: newAccount.email.trim(),
          password: passwordToSave,
          encrypted: true
        };
        
        const accountsCollectionRef = collection(firestore, 'world_users', userId, 'emailAccounts');
        await addDoc(accountsCollectionRef, accountToSave);
        
        // Die Logik für `updateQueueDurationInEmailSettings` muss eventuell auch angepasst werden,
        // falls sie noch auf die Realtime DB zugreift. Fürs Erste lassen wir sie.
        await updateQueueDurationInEmailSettings(accountToSave.email, accountToSave.queue_duration);
        
        setMessage('Email account added successfully.');
        setShowAddAccount(false);
        setNewAccount({
          email: '',
          password: '',
          imap_host: 'imap.hostinger.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.hostinger.com',
          smtp_port: '465',
          smtp_encryption: 'true',
          sender_name: '',
          text_signature: '',
          queue_duration: '10',
          sleep_time_start: '22:00',
          sleep_time_end: '08:00',
          skip_weekends: false
        });
      } catch (error) {
        console.error('Fehler beim Hinzufügen des E-Mail-Kontos in Firestore:', error);
        setMessage(`Fehler: ${error.message}`);
      } finally {
        setIsSavingAccount(false);
      }
    };

    const handleConnectMicrosoft = async () => {
      if (isAccountLimitReached) {
        setMessage(`Account limit reached. Your plan allows up to ${accountLimit} email accounts. Upgrade to add more.`);
        return;
      }
      try {
        setConnectingMicrosoft(true);
        setMessage('Connecting to Microsoft...');
        
        const getMicrosoftAuthUrl = httpsCallable(functions, 'getMicrosoftAuthUrl');
        const result = await getMicrosoftAuthUrl();
        
        if (result.data && result.data.authUrl) {
          // Redirect zu Microsoft Login
          window.location.href = result.data.authUrl;
        } else {
          throw new Error('No auth URL received');
        }
      } catch (error) {
        console.error('Error connecting Microsoft account:', error);
        setMessage(`Error: ${error.message}`);
        setConnectingMicrosoft(false);
      }
    };

    // Funktion zum Aktualisieren eines bestehenden E-Mail-Kontos in Firestore
    const handleUpdateAccount = async () => {
      if (!selectedAccount) return;
      if (isSavingAccount) return;
      
      try {
        setIsSavingAccount(true);
        setMessage('Aktualisiere E-Mail-Konto...');
        
        const { id, ...accountData } = selectedAccount;
        
        let passwordToSave = accountData.password;
        if (accountData.password && !isPasswordEncrypted(accountData.password)) {
          console.log('🔐 Encrypting password before updating...');
          passwordToSave = await encryptEmailPassword(accountData.password);
          console.log('✅ Password encrypted successfully');
        }

        const accountToUpdate = {
            ...accountData,
            password: passwordToSave,
            encrypted: true
        };
        
        const accountDocRef = doc(firestore, 'world_users', userId, 'emailAccounts', id);
        await updateDoc(accountDocRef, accountToUpdate);
        
        await updateQueueDurationInEmailSettings(accountToUpdate.email, accountToUpdate.queue_duration);
        
        setMessage('E-Mail-Account updated successfully.');
        setSelectedAccount(null);
      } catch (error) {
        console.error('Error updating E-Mail-Account in Firestore:', error);
        setMessage(`Error: ${error.message}`);
      } finally {
        setIsSavingAccount(false);
      }
    };

    // Funktion zum Entfernen der Verzögerungsdauer aus den E-Mail-Einstellungen
    const removeQueueDurationFromEmailSettings = async (email) => {
      try {
        // Sanitize die E-Mail-Adresse für die Verwendung als Schlüssel
        const sanitizedEmail = email.replace(/[.#$\/\[\]]/g, '_');
        const durationKey = `email_queue_duration_${sanitizedEmail}`;
        
        // Referenz zu den E-Mail-Einstellungen
        const emailSettingsRef = ref(database, `/World/${userId}/settings/email`);
        
        // Hole die aktuellen Einstellungen
        const snapshot = await get(emailSettingsRef);
        if (!snapshot.exists()) return;
        
        const currentSettings = snapshot.val();
        
        // Prüfe, ob die Einstellung existiert
        if (!(durationKey in currentSettings)) return;
        
        // Entferne die Verzögerungsdauer für diesen Absender
        const { [durationKey]: _, ...updatedSettings } = currentSettings;
        
        // Aktualisiere die Einstellungen
        await set(emailSettingsRef, updatedSettings);
        
        console.log(`Verzögerungsdauer für ${email} wurde entfernt`);
      } catch (error) {
        console.error('Fehler beim Entfernen der Verzögerungsdauer:', error);
      }
    };

    // Funktion zum Löschen eines E-Mail-Kontos aus Firestore
    const handleDeleteAccount = (account) => {
      if (!account) return;
      
      setConfirmModal({
        open: true,
        title: "Delete Email Account",
        message: `Are you sure you want to delete the email account ${account.email}?`,
        onConfirm: async () => {
          setConfirmModal(prev => ({ ...prev, open: false }));
          try {
            setMessage('Deleting email account...');
            
            const accountDocRef = doc(firestore, 'world_users', userId, 'emailAccounts', account.id);
            await deleteDoc(accountDocRef);
            
            await removeQueueDurationFromEmailSettings(account.email);
            
            if (selectedAccount && selectedAccount.id === account.id) {
              setSelectedAccount(null);
            }
            
            setMessage('Email account successfully deleted.');
          } catch (error) {
            console.error('Fehler beim Löschen des E-Mail-Kontos aus Firestore:', error);
            setMessage(`Error: ${error.message}`);
          }
        }
      });
    };

    // Funktion zum Testen der E-Mail-Verbindung
    const handleTestConnection = async (account) => {
      if (!account) return;
      
      try {
        setMessage('Teste Verbindung...');
        
        // Sende eine Anfrage an die Cloud Function, um die Verbindung zu testen
        const response = await authenticatedFetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/testEmailConnection', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: account.email,
            password: account.password,
            imapHost: account.imap_host,
            imapPort: account.imap_port,
            smtpHost: account.smtp_host,
            smtpPort: account.smtp_port,
            encryptionType: account.encryption_type,
            smtpEncryption: account.smtp_encryption
          }),
        });
        
        const data = await response.json();
        
        if (data.success) {
          setMessage('Verbindung erfolgreich getestet!');
        } else {
          setMessage(`Verbindungsfehler: ${data.error}`);
        }
      } catch (error) {
        console.error('Fehler beim Testen der Verbindung:', error);
        setMessage(`Fehler: ${error.message}`);
      }
    };

    // Funktion zum Aktualisieren der Werte des ausgewählten Kontos
    const handleAccountChange = (accountId, e) => {
      const { name, value } = e.target;
      
      // Bei queue_duration sicherstellen, dass wir einen gültigen numerischen Wert haben
      const processedValue = name === 'queue_duration' ? 
        (parseInt(value) || 10) : value;
      
      setEmailAccounts(prev => {
        return prev.map(account => {
          if (account.id === accountId) {
            return {
              ...account,
              [name]: processedValue
            };
          }
          return account;
        });
      });
      
      // Aktualisiere auch selectedAccount wenn es das gleiche Konto ist
      if (selectedAccount && selectedAccount.id === accountId) {
        setSelectedAccount(prev => ({
          ...prev,
          [name]: processedValue
        }));
      }
    };

    // Funktion zum Aktualisieren der Werte des neuen Kontos
    const handleNewAccountChange = (e) => {
      const { name, value } = e.target;
      
      // Bei queue_duration sicherstellen, dass wir einen gültigen numerischen Wert haben
      const processedValue = name === 'queue_duration' ? 
        (parseInt(value) || 10) : value;
      
      setNewAccount(prev => ({
        ...prev,
        [name]: processedValue
      }));
    };

    // Funktion zum Setzen der Server-Einstellungen basierend auf dem Anbieter
    const footerPreviewDoc = (footer) => {
      const html = generateEmailFooter({ useMultipleFooters: true, footerList: [footer] });
      const font = footer?.footer_font || 'Helvetica Neue';
      return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
        html,body{margin:0;padding:8px 0 16px;background:transparent;overflow:auto;}
        body{font-family:'${String(font).replace(/'/g, '')}',Helvetica,Arial,sans-serif;font-size:12px;line-height:1.45;color:#334155;}
        img{max-width:100%;height:auto;}
        a{color:#4a90e2;text-decoration:none;}
      </style></head><body>${html}</body></html>`;
    };

    const renderTextSnippetField = (value, onChange) => (
      <section className="eamCard">
        <h5 className="eamCardTitle">Text snippet</h5>
        <p className="eamHint">Inserted into the email body as {'{{footer_text}}'} when this mailbox generates a mail. Line breaks stay as typed. Separate from the HTML footer.</p>
        <textarea
          name="text_signature"
          className="eamTextSnippet"
          value={value || ''}
          onChange={onChange}
          placeholder={"Viele Grüße,\nSender"}
          rows={4}
          spellCheck="true"
        />
      </section>
    );

    const renderFooterPicker = (value, onSelect) => {
      if (!useMultipleFooters || !footerList.length) return null;
      const active = footerList.filter((f) => f && f.isActive !== false);
      return (
        <section className="eamCard eamFooterPicker">
          <h5 className="eamCardTitle">Email footer</h5>
          <p className="eamHint">Used on every mail sent from this inbox.</p>
          <div className="eamFooterGrid">
            <button
              type="button"
              className={`eamFooterChoice eamFooterChoice--default${!value ? ' is-selected' : ''}`}
              onClick={() => onSelect('')}
              aria-pressed={!value}
            >
              <span className="eamFooterHead">
                <span className="eamFooterRadio" aria-hidden="true" />
                <span className="eamFooterTitles">
                  <span className="eamFooterDefaultMark">Default</span>
                  <span className="eamFooterDefaultSub">First active footer</span>
                </span>
                {!value && <span className="eamFooterBadge">In use</span>}
              </span>
            </button>
            {active.map((footer) => (
              <button
                key={footer.id}
                type="button"
                className={`eamFooterChoice${value === footer.id ? ' is-selected' : ''}`}
                onClick={() => onSelect(footer.id)}
                aria-pressed={value === footer.id}
              >
                <span className="eamFooterHead">
                  <span className="eamFooterRadio" aria-hidden="true" />
                  <span className="eamFooterChoiceName">{footer.name || 'Unnamed footer'}</span>
                  {value === footer.id && <span className="eamFooterBadge">In use</span>}
                </span>
                <span className="eamFooterPreview">
                  <iframe
                    title=""
                    tabIndex={-1}
                    srcDoc={footerPreviewDoc(footer)}
                  />
                </span>
              </button>
            ))}
          </div>
        </section>
      );
    };

    const detectEmailProvider = (account) => {
      const host = `${account?.imap_host || ''} ${account?.smtp_host || ''}`.toLowerCase();
      if (host.includes('hostinger')) return 'hostinger';
      if (host.includes('1und1') || host.includes('ionos')) return 'ionos';
      if (host.includes('gmail')) return 'gmail';
      if (host.includes('outlook') || host.includes('office365')) return 'outlook';
      if (host.includes('strato')) return 'strato';
      if (host.includes('mailbox.org')) return 'mailboxorg';
      if (host.includes('zoho')) return 'zoho';
      if (host.includes('fastmail') || host.includes('messagingengine')) return 'fastmail';
      return null;
    };

    const setServerSettings = (provider, isNewAccount = false) => {
      const settings = {
        hostinger: {
          imap_host: 'imap.hostinger.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.hostinger.com',
          smtp_port: '465',
          smtp_encryption: 'true'
        },
        ionos: {
          imap_host: 'imap.1und1.de',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.1und1.de',
          smtp_port: '587',
          smtp_encryption: 'false'
        },
        outlook: {
          imap_host: 'outlook.office365.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.office365.com',
          smtp_port: '587',
          smtp_encryption: 'false'
        },
        gmail: {
          imap_host: 'imap.gmail.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.gmail.com',
          smtp_port: '587',
          smtp_encryption: 'false'
        },
        strato: {
          imap_host: 'imap.strato.de',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.strato.de',
          smtp_port: '465',
          smtp_encryption: 'true'
        },
        mailboxorg: {
          imap_host: 'imap.mailbox.org',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.mailbox.org',
          smtp_port: '465',
          smtp_encryption: 'true'
        },
        zoho: {
          imap_host: 'imappro.zoho.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtppro.zoho.com',
          smtp_port: '465',
          smtp_encryption: 'true'
        },
        fastmail: {
          imap_host: 'imap.fastmail.com',
          imap_port: '993',
          encryption_type: 'SSL',
          smtp_host: 'smtp.fastmail.com',
          smtp_port: '465',
          smtp_encryption: 'true'
        }
      };
      
      if (isNewAccount) {
        setNewAccount(prev => ({
          ...prev,
          ...settings[provider]
        }));
      } else if (selectedAccount) {
        setSelectedAccount(prev => ({
          ...prev,
          ...settings[provider]
        }));
      }
    };

    // EmailLogsModal Komponente
    const EmailLogsModal = () => {
      if (!showLogsModal || !selectedAccountLogs) return null;
      
      const logs = emailLogs[selectedAccountLogs.email] || [];
      
      const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      };
      
      const getStatusIcon = (status) => {
        switch (status) {
          case 'success':
            return '✅';
          case 'error':
            return '❌';
          case 'processing':
            return '⏳';
          case 'info':
            return 'ℹ️';
          default:
            return '📧';
        }
      };
      
      const getStatusColor = (status) => {
        switch (status) {
          case 'success':
            return '#10b981';
          case 'error':
            return '#ef4444';
          case 'processing':
            return '#f59e0b';
          case 'info':
            return '#3b82f6';
          default:
            return '#6b7280';
        }
      };
      
      return (
        <div className="emailLogsModalOverlay" onClick={() => setShowLogsModal(false)}>
          <div className="emailLogsModalContainer" onClick={(e) => e.stopPropagation()}>
            <div className="emailLogsModalHeader">
              <h3>📧 Email Logs: {selectedAccountLogs.email}</h3>
              <button 
                className="emailLogsModalCloseButton"
                onClick={() => setShowLogsModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="emailLogsModalContent">
              {logs.length === 0 ? (
                <div className="emailLogsEmpty">
                  <div className="emailLogsEmptyIcon">📭</div>
                  <p>No logs available for this email account.</p>
                  <small>Logs will appear here after sending emails.</small>
                </div>
              ) : (
                <div className="emailLogsList">
                  {logs.map((log) => (
                    <div 
                      key={log.id} 
                      className={`emailLogItem ${log.status}`}
                      style={{ borderLeftColor: getStatusColor(log.status) }}
                    >
                      <div className="emailLogHeader">
                        <div className="emailLogStatus">
                          <span className="emailLogStatusIcon">{getStatusIcon(log.status)}</span>
                          <span className="emailLogStatusText" style={{ color: getStatusColor(log.status) }}>
                            {log.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="emailLogDate">
                          {formatDate(log.timestamp)}
                        </div>
                      </div>
                      
                      <div className="emailLogMessage">
                        {log.message}
                      </div>
                      
                      {log.recipientEmail && (
                        <div className="emailLogDetail">
                          <strong>To:</strong> {log.recipientEmail}
                        </div>
                      )}
                      
                      {log.subject && (
                        <div className="emailLogDetail">
                          <strong>Subject:</strong> {log.subject}
                        </div>
                      )}
                      
                      {log.emailType && (
                        <div className="emailLogDetail">
                          <strong>Type:</strong> {log.emailType}
                        </div>
                      )}
                      
                      {log.duration && (
                        <div className="emailLogDetail">
                          <strong>Duration:</strong> {log.duration}ms
                        </div>
                      )}
                      
                      {log.messageId && (
                        <div className="emailLogDetail">
                          <strong>Message ID:</strong> {log.messageId}
                        </div>
                      )}
                      
                      {log.errorCode && (
                        <div className="emailLogDetail emailLogError">
                          <strong>Error Code:</strong> {log.errorCode}
                        </div>
                      )}
                      
                      {log.errorResponse && (
                        <div className="emailLogDetail emailLogError">
                          <strong>Error Response:</strong> {log.errorResponse}
                        </div>
                      )}
                      
                      {log.previewUrl && (
                        <div className="emailLogDetail">
                          <strong>Preview:</strong> 
                          <a href={log.previewUrl} target="_blank" rel="noopener noreferrer">
                            View Email
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // EmailHealthModal — Domain-Auth + Warmup + Zustellbarkeit auf einen Blick
    const EmailHealthModal = () => {
      if (!showHealthModal) return null;

      const r = healthResult || {};
      const dom = r.domain || null;
      const warmup = r.warmup || null;
      const warmupConfig = r.warmupConfig || {};
      const deliver = r.deliverability || {};
      const accEmail = healthAccount?.email || '';

      const checkRows = dom?.checks ? [
        { key: 'spf', label: 'SPF' },
        { key: 'dkim', label: 'DKIM' },
        { key: 'dmarc', label: 'DMARC' },
        { key: 'mx', label: 'MX' },
      ] : [];

      const totalSent = deliver.totalSent || 0;
      const totalBounced = deliver.totalBounced || 0;
      const totalComplained = deliver.totalComplained || 0;
      const totalReplied = deliver.totalReplied || 0;
      const bounceRate = totalSent ? (totalBounced / totalSent) * 100 : 0;
      const replyRate = totalSent ? (totalReplied / totalSent) * 100 : 0;
      const warmupEnabled = warmupConfig.enabled === true;

      const recs = [];
      if (dom?.checks) {
        if (dom.checks.dkim?.status === 'fail') recs.push({ level: 'fail', text: 'DKIM ist nicht eingerichtet — beim E-Mail-Provider DKIM-Signierung aktivieren. Größter Hebel gegen Spam & Policy-Rejects (554 5.7.1).' });
        if (dom.checks.spf?.status === 'fail') recs.push({ level: 'fail', text: 'SPF fehlt — TXT-Record mit v=spf1 für die Domain setzen.' });
        else if (dom.checks.spf?.status === 'warn') recs.push({ level: 'warn', text: (dom.checks.spf.hints && dom.checks.spf.hints[0]) || 'SPF-Record hat Auffälligkeiten.' });
        if (dom.checks.dmarc?.status === 'fail') recs.push({ level: 'fail', text: 'DMARC fehlt — TXT-Record bei _dmarc anlegen (mind. p=quarantine).' });
        else if (dom.checks.dmarc?.status === 'warn') recs.push({ level: 'warn', text: 'DMARC steht auf p=none (keine Durchsetzung) — auf quarantine/reject erhöhen.' });
        if (dom.checks.mx?.status === 'fail') recs.push({ level: 'fail', text: 'Keine MX-Records gefunden — die Domain kann keine Mails empfangen.' });
      }
      if (!warmupEnabled) recs.push({ level: 'warn', text: 'Warmup ist für diesen Account aus — aktivieren, um Reputation aufzubauen und Inbox-Platzierung zu verbessern.' });
      else if (warmup && typeof warmup.score === 'number' && warmup.score < 55) recs.push({ level: 'info', text: `Warmup läuft (${warmup.phase}, ${warmup.score}/100) — Account noch nicht voll aufgewärmt. Sendevolumen niedrig halten.` });
      if (totalSent >= 20 && bounceRate > 2) recs.push({ level: 'fail', text: `Bounce-Rate ${bounceRate.toFixed(1)}% ist hoch (>2%) — Adressqualität / Listenhygiene prüfen.` });
      if (recs.length === 0 && !healthLoading) recs.push({ level: 'pass', text: 'Keine kritischen Probleme gefunden.' });

      const phaseLabel = (p) => ({ off: 'Aus', cold: 'Kalt', warming: 'Wärmt auf', warm: 'Warm', hot: 'Heiß' }[p] || p || '—');

      return (
        <div className="emailHealthModalOverlay" onClick={() => setShowHealthModal(false)}>
          <div className="emailHealthModalContainer" onClick={(e) => e.stopPropagation()}>
            <div className="emailHealthModalHeader">
              <h3>🩺 Account Health: {accEmail}</h3>
              <button className="emailHealthModalCloseButton" onClick={() => setShowHealthModal(false)}>×</button>
            </div>

            <div className="emailHealthModalContent">
              {healthLoading ? (
                <div className="emailHealthLoading">
                  <div className="emailHealthSpinner" />
                  <p>Prüfe Domain-Authentifizierung, Warmup &amp; Zustellbarkeit…</p>
                </div>
              ) : (
                <>
                  <div className="emailHealthSection">
                    <div className="emailHealthSectionHead">
                      <h4>Domain-Authentifizierung</h4>
                      {dom && typeof dom.score === 'number' && (
                        <span className={`domainHealthBadge ${dom.overall || 'unavailable'}`}>Score {dom.score}/100</span>
                      )}
                    </div>
                    {!dom ? (
                      <p className="emailHealthMuted">Domain-Check nicht verfügbar.</p>
                    ) : (
                      <div className="emailHealthChecks">
                        {checkRows.map(({ key, label }) => {
                          const c = dom.checks[key] || {};
                          return (
                            <div key={key} className="emailHealthCheckRow">
                              <span className={`domainHealthBadge ${c.status || 'unavailable'}`}>{label}</span>
                              <span className="emailHealthCheckMsg">{c.message || '—'}</span>
                              {Array.isArray(c.hints) && c.hints.length > 0 && (
                                <span className="emailHealthCheckHint">{c.hints[0]}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="emailHealthSection">
                    <div className="emailHealthSectionHead">
                      <h4>Warmup</h4>
                      <span className={`domainHealthBadge ${warmupEnabled ? 'pass' : 'unavailable'}`}>{warmupEnabled ? 'Aktiv' : 'Aus'}</span>
                    </div>
                    {warmupEnabled && warmup ? (
                      <div className="emailHealthStats">
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{warmup.score}/100</span><span className="emailHealthStatLbl">Warmup-Score</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{phaseLabel(warmup.phase)}</span><span className="emailHealthStatLbl">Phase</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{warmup.daysSinceStart ?? 0} T</span><span className="emailHealthStatLbl">Seit Start</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{warmup.details?.totalSent ?? 0}</span><span className="emailHealthStatLbl">Warmup-Sends</span></div>
                      </div>
                    ) : (
                      <p className="emailHealthMuted">Warmup ist für diesen Account nicht aktiv.</p>
                    )}
                  </div>

                  <div className="emailHealthSection">
                    <div className="emailHealthSectionHead">
                      <h4>Zustellbarkeit (echte Sends)</h4>
                    </div>
                    {totalSent > 0 ? (
                      <div className="emailHealthStats">
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{totalSent}</span><span className="emailHealthStatLbl">Gesendet</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal" style={{ color: bounceRate > 2 ? '#dc2626' : '#16a34a' }}>{bounceRate.toFixed(1)}%</span><span className="emailHealthStatLbl">Bounce-Rate</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal">{replyRate.toFixed(1)}%</span><span className="emailHealthStatLbl">Antwort-Rate</span></div>
                        <div className="emailHealthStat"><span className="emailHealthStatVal" style={{ color: totalComplained > 0 ? '#dc2626' : '#16a34a' }}>{totalComplained}</span><span className="emailHealthStatLbl">Beschwerden</span></div>
                      </div>
                    ) : (
                      <p className="emailHealthMuted">Noch keine Sende-Daten für diesen Account.</p>
                    )}
                  </div>

                  <div className="emailHealthSection">
                    <div className="emailHealthSectionHead">
                      <h4>Empfehlungen</h4>
                    </div>
                    <ul className="emailHealthRecs">
                      {recs.map((rec, i) => (
                        <li key={i} className={`emailHealthRec ${rec.level}`}>{rec.text}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="settingsGroup1">
        <h3>Manage Email Accounts</h3>
        
        {isLoading ? (
          <p></p>
        ) : (
          <>
            <div className="emailAccountsList">
              {emailAccounts.length === 0 ? (
                <div className="noEmailAccounts">
                  <div className="noEmailAccounts-content">
                    <div className="noEmailAccounts-icon">📧</div>
                    <h4>No Email Accounts Configured</h4>
                    <p>Please add your first email account to start sending emails.</p>
                    <button 
                      className="addEmailAccountButton primary-button"
                      onClick={() => setShowAddAccount(true)}
                    >
                      + Add Email Account
                    </button>
                  </div>
                </div>

              ) : (
                emailAccounts.map((account, index) => (
                  <div 
                    key={index} 
                    className={`emailAccountItem ${selectedAccount && selectedAccount.id === account.id ? 'selected' : ''} ${openKebabId === account.id ? 'kebab-open' : ''}`}
                    onClick={() => { setOpenKebabId(null); setSelectedAccount(selectedAccount && selectedAccount.id === account.id ? null : account); }}
                  >
                    <div className="emailAccountInfo">
                      {account.sender_name && (
                        <div className="emailAccountName">{account.sender_name}</div>
                      )}
                      <div className="emailAccountAddress">
                        {account.provider_type === 'microsoft_graph' && (
                          <span className="microsoft-badge" title="Connected via Microsoft OAuth">
                            <svg width="14" height="14" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px', marginBottom: '-2px'}}>
                              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                            </svg>
                          </span>
                        )}
                        {account.email}
                        <span className={`emailAccountStatus ${getAccountLogStatus(account.email)}`}>
                          {getAccountLogStatus(account.email) === 'success' && <img src="/img/mail_check.svg" alt="Success" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                          {getAccountLogStatus(account.email) === 'error' && <img src="/img/mail_warning.svg" alt="Warning" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                          {getAccountLogStatus(account.email) === 'info' && <img src="/img/mail_info.svg" alt="Info" style={{width: '20px', height: '20px', marginBottom: '-5px'}} />}
                        </span>
                      </div>
                      <div className="emailAccountMeta">
                        <span className="emailAccountServer">{account.smtp_host}</span>
                        {account.provider_type === 'microsoft_graph' && <span className="emailAccountProviderTag">Microsoft</span>}
                        {account.ecp_max_per_day && <span className="emailAccountCapacity">{account.ecp_max_per_day}/day</span>}
                      </div>
                    </div>
                    <div className="emailAccountActions">
                      <div className="emailAccountKebab" style={{position: 'relative'}}>
                        <button 
                          className="emailAccountKebabBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenKebabId(openKebabId === account.id ? null : account.id);
                          }}
                          title="More options"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                            <circle cx="8" cy="3" r="1.5"/>
                            <circle cx="8" cy="8" r="1.5"/>
                            <circle cx="8" cy="13" r="1.5"/>
                          </svg>
                        </button>
                        {openKebabId === account.id && (
                          <>
                            <div className="emailAccountKebabOverlay" onClick={(e) => { e.stopPropagation(); setOpenKebabId(null); }} />
                            <div className="emailAccountKebabMenu">
                              <button onClick={(e) => {
                                e.stopPropagation();
                                setOpenKebabId(null);
                                handleShowLogs(account);
                              }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                View Logs
                              </button>
                              <button onClick={(e) => {
                                e.stopPropagation();
                                setOpenKebabId(null);
                                handleCheckHealth(account);
                              }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 11 12 14 16 9"/></svg>
                                Check Health
                              </button>
                              <button className="emailAccountKebabDanger" onClick={(e) => {
                                e.stopPropagation();
                                setOpenKebabId(null);
                                handleDeleteAccount(account);
                              }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                Delete Account
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {emailAccounts.length > 0 && !showAddAccount && (
              <div className="emailAccountsLimitRow">
                <button 
                  className={`addEmailAccountButton ${isAccountLimitReached ? 'at-limit' : ''}`}
                  style={{ marginBottom: '0' }}
                  disabled={isAccountLimitReached}
                  onClick={() => {
                    if (isAccountLimitReached) return;
                    setSelectedAccount(null);
                    setShowAddAccount(true);
                  }}
                >
                  {isAccountLimitReached ? 'Account Limit Reached' : '+ Add More Email Accounts'}
                </button>
                <span className="emailAccountsLimitBadge">
                  {emailAccounts.length} / {accountLimit}
                </span>
              </div>
            )}
            
            {message && <div className="emailAccountMessage">{message}</div>}
            
            {(selectedAccount || showAddAccount) && createPortal(
              <div
                className="emailAccountModalOverlay"
                onClick={() => {
                  setSelectedAccount(null);
                  setShowAddAccount(false);
                }}
                role="presentation"
              >
              <div className="emailAccountModalBackdrop" aria-hidden="true" />
            {selectedAccount && (
              <div className="emailAccountDetails emailAccountDetails--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="email-account-edit-title">
                <div className="emailAccountDetailsHeader">
                  <div className="emailAccountDetailsHeading">
                    <span className="emailAccountDetailsKicker">Mailbox</span>
                    <h4 id="email-account-edit-title">Edit Email Account</h4>
                    {selectedAccount.email && (
                      <p className="emailAccountDetailsLead">{selectedAccount.email}</p>
                    )}
                  </div>
                  <button 
                    className="emailAccountDetailsClose"
                    onClick={() => setSelectedAccount(null)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div className="emailAccountDetailsContent">
                  <div className="eamGrid">
                    <div className="eamCol">
                      <section className="eamCard">
                        <h5 className="eamCardTitle">Identity</h5>
                        <div className="settingField">
                          <label>Email address</label>
                          <input
                            type="email"
                            name="email"
                            value={selectedAccount.email}
                            onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            placeholder="your.email@example.com"
                          />
                        </div>
                        <div className="settingField">
                          <label>Password</label>
                          <input
                            type="password"
                            name="password"
                            value={selectedAccount.password}
                            onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            placeholder="Your email password"
                          />
                        </div>
                        <div className="settingField">
                          <label>Sender name</label>
                          <input
                            type="text"
                            name="sender_name"
                            value={selectedAccount.sender_name || ''}
                            onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            placeholder="Name displayed as sender"
                          />
                        </div>
                      </section>

                      <section className="eamCard">
                        <h5 className="eamCardTitle">Provider &amp; servers</h5>
                        <p className="eamHint">Pick a host to fill IMAP and SMTP, then adjust if needed.</p>
                        <div className="emailServerTabs eamProviderTabs">
                          {[
                            { id: 'hostinger', label: 'Hostinger' },
                            { id: 'ionos', label: '1&1 / IONOS' },
                            { id: 'gmail', label: 'Google / Gmail' },
                            { id: 'outlook', label: 'Outlook' },
                            { id: 'strato', label: 'Strato' },
                            { id: 'mailboxorg', label: 'Mailbox.org' },
                            { id: 'zoho', label: 'Zoho Mail' },
                            { id: 'fastmail', label: 'Fastmail' },
                          ].map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              className={`emailServerTab${detectEmailProvider(selectedAccount) === p.id ? ' active' : ''}`}
                              onClick={() => setServerSettings(p.id)}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <div className="eamServerGrid">
                          <div className="settingField">
                            <label>IMAP host</label>
                            <input
                              type="text"
                              name="imap_host"
                              value={selectedAccount.imap_host}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                              placeholder="imap.example.com"
                            />
                          </div>
                          <div className="settingField">
                            <label>IMAP port</label>
                            <input
                              type="text"
                              name="imap_port"
                              value={selectedAccount.imap_port}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                              placeholder="993"
                            />
                          </div>
                          <div className="settingField">
                            <label>IMAP encryption</label>
                            <select
                              name="encryption_type"
                              value={selectedAccount.encryption_type}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            >
                              <option value="SSL">SSL</option>
                              <option value="TLS">TLS</option>
                              <option value="STARTTLS">STARTTLS</option>
                            </select>
                          </div>
                          <div className="settingField">
                            <label>SMTP host</label>
                            <input
                              type="text"
                              name="smtp_host"
                              value={selectedAccount.smtp_host}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                              placeholder="smtp.example.com"
                            />
                          </div>
                          <div className="settingField">
                            <label>SMTP port</label>
                            <input
                              type="text"
                              name="smtp_port"
                              value={selectedAccount.smtp_port}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                              placeholder="587"
                            />
                          </div>
                          <div className="settingField">
                            <label>SMTP encryption</label>
                            <select
                              name="smtp_encryption"
                              value={selectedAccount.smtp_encryption}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            >
                              <option value="false">None / STARTTLS</option>
                              <option value="true">SSL / TLS</option>
                            </select>
                          </div>
                        </div>
                      </section>
                    </div>

                    <div className="eamCol">
                      <section className="eamCard eamCard--pace">
                        <div className="ecp-header">
                          <h5 className="eamCardTitle">Sending speed</h5>
                          <span className="ecp-plan-badge">
                            {EMAIL_PLANS[emailPlan].label} · {dailyEmailLimit}/day
                          </span>
                        </div>
                        <div className="ecp-slider-box">
                          <div className="ecp-value-display">
                            <span className="ecp-value-number">
                              {Math.min(calculateEmailsPerDay(parseInt(selectedAccount.queue_duration) || 32), calculateMaxEmailsForAccount(selectedAccount.id))}
                            </span>
                            <span className="ecp-value-label">emails / day</span>
                          </div>
                          <div className="ecp-slider-wrapper">
                            <input
                              type="range"
                              min="1"
                              max={calculateMaxEmailsForAccount(selectedAccount.id)}
                              value={Math.min(calculateEmailsPerDay(parseInt(selectedAccount.queue_duration) || 32), calculateMaxEmailsForAccount(selectedAccount.id))}
                              onChange={(e) => {
                                const emailsPerDay = parseInt(e.target.value);
                                const activeMinutesPerDay = 14 * 60;
                                const delayMinutes = Math.ceil(activeMinutesPerDay / emailsPerDay);
                                const minDelay = calculateMinDelayForAccount(selectedAccount.id);
                                const finalDelay = Math.max(delayMinutes, minDelay);
                                handleAccountChange(selectedAccount.id, {
                                  target: { name: 'queue_duration', value: finalDelay.toString() }
                                });
                              }}
                              className="ecp-slider"
                            />
                            <div className="ecp-slider-labels">
                              <span>1</span>
                              <span>{calculateMaxEmailsForAccount(selectedAccount.id)}</span>
                            </div>
                          </div>
                          <div className="ecp-delay-info">
                            ≈ {selectedAccount.queue_duration || 32} min delay (±30% random)
                          </div>
                        </div>
                        <div className="ecp-total-box">
                          <div className="ecp-total-bar">
                            <div
                              className="ecp-total-bar-fill"
                              style={{
                                width: `${Math.min(100, (calculateTotalCapacity(emailAccounts) / dailyEmailLimit) * 100)}%`
                              }}
                            />
                          </div>
                          <div className="ecp-total-text">
                            <strong>{calculateTotalCapacity(emailAccounts)}</strong> / {dailyEmailLimit} across all inboxes
                            {calculateTotalCapacity(emailAccounts) > dailyEmailLimit && (
                              <span className="ecp-warning">Over limit</span>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="eamCard">
                        <h5 className="eamCardTitle">Quiet hours</h5>
                        <p className="eamHint">German time (CET / CEST). Mail in this window waits until it ends.</p>
                        <div className="sleepTimeInputs">
                          <div className="settingField">
                            <label>From</label>
                            <input
                              type="time"
                              name="sleep_time_start"
                              value={selectedAccount.sleep_time_start || '22:00'}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            />
                          </div>
                          <div className="settingField">
                            <label>To</label>
                            <input
                              type="time"
                              name="sleep_time_end"
                              value={selectedAccount.sleep_time_end || '08:00'}
                              onChange={(e) => handleAccountChange(selectedAccount.id, e)}
                            />
                          </div>
                        </div>
                        {selectedAccount.sleep_time_end === "00:00" && (
                          <p className="eamWarn">Midnight as 00:00 can break delivery — use 23:59 or 00:01.</p>
                        )}
                      </section>

                      <section className="eamCard skipWeekendsToggle">
                        <label className="skipWeekendsLabel">
                          <span className="skipWeekendsText">
                            <strong>Skip weekends</strong>
                            <span className="skipWeekendsHint">
                              Saturday and Sunday sends move to Monday morning.
                            </span>
                          </span>
                          <div
                            className={`toggleSwitch ${selectedAccount.skip_weekends ? 'active' : ''}`}
                            onClick={() => handleAccountChange(selectedAccount.id, {
                              target: { name: 'skip_weekends', value: !selectedAccount.skip_weekends }
                            })}
                            role="switch"
                            aria-checked={!!selectedAccount.skip_weekends}
                          >
                            <div className="toggleSlider" />
                          </div>
                        </label>
                      </section>
                      {renderTextSnippetField(
                        selectedAccount.text_signature,
                        (e) => handleAccountChange(selectedAccount.id, e)
                      )}
                      {renderFooterPicker(selectedAccount.footerId || '', (footerId) => {
                        handleAccountChange(selectedAccount.id, { target: { name: 'footerId', value: footerId } });
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="emailAccountButtons">
                  <button 
                    className="cancelEmailAccountButton"
                    onClick={() => setSelectedAccount(null)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="saveEmailAccountButton"
                    onClick={handleUpdateAccount}
                    disabled={isSavingAccount}
                  >
                    {isSavingAccount ? (
                      <>
                        <span className="saveEmailAccountSpinner" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {showAddAccount && (
              <div className="emailAccountDetails emailAccountDetails--wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="email-account-add-title">
                <div className="emailAccountDetailsHeader">
                  <div className="emailAccountDetailsHeading">
                    <span className="emailAccountDetailsKicker">Mailbox</span>
                    <h4 id="email-account-add-title">Add Email Account</h4>
                  </div>
                  <button 
                    className="emailAccountDetailsClose"
                    onClick={() => setShowAddAccount(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                
                <div className="emailAccountDetailsContent">
                  <div className="eamGrid">
                    <div className="eamCol">
                      <section className="eamCard">
                        <h5 className="eamCardTitle">Identity</h5>
                        <div className="settingField">
                          <label>Email address</label>
                          <input
                            type="email"
                            name="email"
                            value={newAccount.email}
                            onChange={handleNewAccountChange}
                            placeholder="your.email@example.com"
                          />
                        </div>
                        <div className="settingField">
                          <label>Password</label>
                          <input
                            type="password"
                            name="password"
                            value={newAccount.password}
                            onChange={handleNewAccountChange}
                            placeholder="Your email password"
                          />
                        </div>
                        <div className="settingField">
                          <label>Sender name</label>
                          <input
                            type="text"
                            name="sender_name"
                            value={newAccount.sender_name || ''}
                            onChange={handleNewAccountChange}
                            placeholder="Name displayed as sender"
                          />
                        </div>
                      </section>

                      <section className="eamCard">
                        <h5 className="eamCardTitle">Provider &amp; servers</h5>
                        <p className="eamHint">Pick a host to fill IMAP and SMTP, then adjust if needed.</p>
                        <div className="emailServerTabs eamProviderTabs">
                          {[
                            { id: 'hostinger', label: 'Hostinger' },
                            { id: 'ionos', label: '1&1 / IONOS' },
                            { id: 'gmail', label: 'Google / Gmail' },
                            { id: 'outlook', label: 'Outlook' },
                            { id: 'strato', label: 'Strato' },
                            { id: 'mailboxorg', label: 'Mailbox.org' },
                            { id: 'zoho', label: 'Zoho Mail' },
                            { id: 'fastmail', label: 'Fastmail' },
                          ].map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              className={`emailServerTab${detectEmailProvider(newAccount) === p.id ? ' active' : ''}`}
                              onClick={() => setServerSettings(p.id, true)}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <div className="eamServerGrid">
                          <div className="settingField">
                            <label>IMAP host</label>
                            <input type="text" name="imap_host" value={newAccount.imap_host} onChange={handleNewAccountChange} placeholder="imap.example.com" />
                          </div>
                          <div className="settingField">
                            <label>IMAP port</label>
                            <input type="text" name="imap_port" value={newAccount.imap_port} onChange={handleNewAccountChange} placeholder="993" />
                          </div>
                          <div className="settingField">
                            <label>IMAP encryption</label>
                            <select name="encryption_type" value={newAccount.encryption_type} onChange={handleNewAccountChange}>
                              <option value="SSL">SSL</option>
                              <option value="TLS">TLS</option>
                              <option value="STARTTLS">STARTTLS</option>
                            </select>
                          </div>
                          <div className="settingField">
                            <label>SMTP host</label>
                            <input type="text" name="smtp_host" value={newAccount.smtp_host} onChange={handleNewAccountChange} placeholder="smtp.example.com" />
                          </div>
                          <div className="settingField">
                            <label>SMTP port</label>
                            <input type="text" name="smtp_port" value={newAccount.smtp_port} onChange={handleNewAccountChange} placeholder="587" />
                          </div>
                          <div className="settingField">
                            <label>SMTP encryption</label>
                            <select name="smtp_encryption" value={newAccount.smtp_encryption} onChange={handleNewAccountChange}>
                              <option value="false">None / STARTTLS</option>
                              <option value="true">SSL / TLS</option>
                            </select>
                          </div>
                        </div>
                      </section>
                    </div>

                    <div className="eamCol">
                      <section className="eamCard eamCard--pace">
                        <div className="ecp-header">
                          <h5 className="eamCardTitle">Sending speed</h5>
                          <span className="ecp-plan-badge">
                            {EMAIL_PLANS[emailPlan].label} · {dailyEmailLimit}/day
                          </span>
                        </div>
                        <div className="ecp-slider-box">
                          <div className="ecp-value-display">
                            <span className="ecp-value-number">
                              {Math.min(calculateEmailsPerDay(parseInt(newAccount.queue_duration) || 32), Math.max(1, dailyEmailLimit - calculateTotalCapacity(emailAccounts)))}
                            </span>
                            <span className="ecp-value-label">emails / day</span>
                          </div>
                          <div className="ecp-slider-wrapper">
                            <input
                              type="range"
                              min="1"
                              max={Math.max(1, dailyEmailLimit - calculateTotalCapacity(emailAccounts))}
                              value={Math.min(calculateEmailsPerDay(parseInt(newAccount.queue_duration) || 32), Math.max(1, dailyEmailLimit - calculateTotalCapacity(emailAccounts)))}
                              onChange={(e) => {
                                const emailsPerDay = parseInt(e.target.value);
                                const activeMinutesPerDay = 14 * 60;
                                const delayMinutes = Math.ceil(activeMinutesPerDay / emailsPerDay);
                                const currentCapacity = calculateTotalCapacity(emailAccounts);
                                const remainingCapacity = Math.max(1, dailyEmailLimit - currentCapacity);
                                const minDelay = Math.ceil(activeMinutesPerDay / remainingCapacity);
                                const finalDelay = Math.max(delayMinutes, Math.max(5, Math.min(500, minDelay)));
                                handleNewAccountChange({
                                  target: { name: 'queue_duration', value: finalDelay.toString() }
                                });
                              }}
                              className="ecp-slider"
                            />
                            <div className="ecp-slider-labels">
                              <span>1</span>
                              <span>{Math.max(1, dailyEmailLimit - calculateTotalCapacity(emailAccounts))}</span>
                            </div>
                          </div>
                          <div className="ecp-delay-info">
                            ≈ {newAccount.queue_duration || 32} min delay (±30% random)
                          </div>
                        </div>
                        <div className="ecp-total-box">
                          <div className="ecp-total-bar">
                            <div
                              className="ecp-total-bar-fill"
                              style={{
                                width: `${Math.min(100, ((calculateTotalCapacity(emailAccounts) + calculateEmailsPerDay(parseInt(newAccount.queue_duration) || 32)) / dailyEmailLimit) * 100)}%`
                              }}
                            />
                          </div>
                          <div className="ecp-total-text">
                            Preview: <strong>{calculateTotalCapacity(emailAccounts) + calculateEmailsPerDay(parseInt(newAccount.queue_duration) || 32)}</strong> / {dailyEmailLimit}
                            {(calculateTotalCapacity(emailAccounts) + calculateEmailsPerDay(parseInt(newAccount.queue_duration) || 32)) > dailyEmailLimit && (
                              <span className="ecp-warning">Over limit</span>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="eamCard">
                        <h5 className="eamCardTitle">Quiet hours</h5>
                        <p className="eamHint">German time (CET / CEST). Mail in this window waits until it ends.</p>
                        <div className="sleepTimeInputs">
                          <div className="settingField">
                            <label>From</label>
                            <input type="time" name="sleep_time_start" value={newAccount.sleep_time_start || '22:00'} onChange={handleNewAccountChange} />
                          </div>
                          <div className="settingField">
                            <label>To</label>
                            <input type="time" name="sleep_time_end" value={newAccount.sleep_time_end || '08:00'} onChange={handleNewAccountChange} />
                          </div>
                        </div>
                        {newAccount.sleep_time_end === "00:00" && (
                          <p className="eamWarn">Midnight as 00:00 can break delivery — use 23:59 or 00:01.</p>
                        )}
                      </section>

                      <section className="eamCard skipWeekendsToggle">
                        <label className="skipWeekendsLabel">
                          <span className="skipWeekendsText">
                            <strong>Skip weekends</strong>
                            <span className="skipWeekendsHint">Saturday and Sunday sends move to Monday morning.</span>
                          </span>
                          <div
                            className={`toggleSwitch ${newAccount.skip_weekends ? 'active' : ''}`}
                            onClick={() => handleNewAccountChange({
                              target: { name: 'skip_weekends', value: !newAccount.skip_weekends }
                            })}
                            role="switch"
                            aria-checked={!!newAccount.skip_weekends}
                          >
                            <div className="toggleSlider" />
                          </div>
                        </label>
                      </section>
                      {renderTextSnippetField(newAccount.text_signature, handleNewAccountChange)}
                      {renderFooterPicker(newAccount.footerId || '', (footerId) => {
                        handleNewAccountChange({ target: { name: 'footerId', value: footerId } });
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="emailAccountButtons">
                  <button 
                    className="cancelEmailAccountButton"
                    onClick={() => {
                      setShowAddAccount(false);
                      setNewAccount({
                        email: '',
                        password: '',
                        imap_host: 'imap.hostinger.com',
                        imap_port: '993',
                        encryption_type: 'SSL',
                        smtp_host: 'smtp.hostinger.com',
                        smtp_port: '465',
                        smtp_encryption: 'true',
                        sender_name: '',
                        text_signature: '',
                        queue_duration: '10',
                        sleep_time_start: '22:00',
                        sleep_time_end: '08:00',
                        skip_weekends: false
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="saveEmailAccountButton"
                    onClick={handleAddAccount}
                    disabled={isSavingAccount}
                  >
                    {isSavingAccount ? (
                      <>
                        <span className="saveEmailAccountSpinner" />
                        Saving...
                      </>
                    ) : (
                      'Add Account'
                    )}
                  </button>
                </div>
              </div>
            )}
              </div>,
              document.body
            )}
          </>
        )}
        
        {/* Email Logs Modal */}
        <EmailLogsModal />

        {/* Account Health Modal */}
        <EmailHealthModal />
      </div>
    );
  };

  const renderEmailTab = () => (
    <div className="settingsGroup1">
      <EmailAccountManager userId={userId} footerList={footerList} useMultipleFooters={emailSettings.useMultipleFooters === true} />
    </div>
  );

  // Accounts, Footer/Signature und Warmup teilen sich einen Sidebar-Eintrag und
  // werden hier über die gemeinsame Settings-Nav umgeschaltet. Der Sub-Tab IST
  // `activeTab`, deshalb bleiben URL und Deep-Links unverändert gültig.
  const renderEmailSetup = () => (
    <>
      <SettingsTabNav
        tabs={EMAIL_SETUP_TABS}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Email setup sections"
      />
      {activeTab === 'emailPreferences' ? renderEmailPreferencesTab()
        : activeTab === 'trueWarmup' ? <TrueWarmup userId={userId} />
        : renderEmailTab()}
    </>
  );

  // Vorlagen und CopyTwin teilen einen Sidebar-Eintrag, siehe TEMPLATE_TABS.
  const renderTemplatesGroup = () => (
    <>
      <SettingsTabNav
        tabs={TEMPLATE_TABS}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Template sections"
      />
      {activeTab === 'copyClones'
        ? <CopyCloneAI userId={userId} />
        : <WorldSettingsTopics userId={userId} />}
    </>
  );

  // SafeGuard, BlackList und Spam Analyzer teilen sich einen Sidebar-Eintrag.
  const renderSafetyGroup = () => (
    <>
      <SettingsTabNav
        tabs={SAFETY_TABS}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Safety sections"
      />
      {activeTab === 'blacklist' ? <BlackList userId={userId} />
        : activeTab === 'spamAnalyzer' ? <SpamCheckSettings userId={userId} />
        : <SafeGuardSettings userId={userId} />}
    </>
  );

  // Auto-Forward, Data Export und die bisherigen Advanced Settings teilen
  // sich einen Sidebar-Eintrag. Owner sehen alle drei Tabs; Team-Mitglieder
  // nur die Automatisierung (die anderen zwei sind Owner-only).
  const renderAdvancedSettingsGroup = () => {
    const tabs = isOwner
      ? ADVANCED_SETTINGS_TABS
      : ADVANCED_SETTINGS_TABS.filter((t) => !t.ownerOnly);
    return (
      <>
        {tabs.length > 1 && (
          <SettingsTabNav
            tabs={tabs}
            activeId={activeTab}
            onChange={setActiveTab}
            ariaLabel="Advanced settings sections"
          />
        )}
        {activeTab === 'autoForward' && isOwner ? <AutoForwardSettings userId={userId} />
          : activeTab === 'dataExport' && isOwner ? <DataExportSettings userId={userId} />
          : activeTab === 'customFields' ? <CustomFieldsSettings userId={userId} />
          : renderAdvancedTab()}
      </>
    );
  };

  // API, CRM, Calendly und WhatsApp teilen sich einen Sidebar-Eintrag mit
  // 4er-Pane-Nav (gleiches Pattern wie Safety/Advanced Settings).
  const renderIntegrationsGroup = () => (
    <>
      <SettingsTabNav
        tabs={INTEGRATIONS_TABS}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Integration sections"
      />
      {activeTab === 'crmIntegrations' ? <CrmIntegrationsSettings userId={userId} />
        : activeTab === 'dataSources' ? <DataConnectorsSettings userId={userId} />
        : activeTab === 'extraSettings' ? <MoreWorldSettings userId={userId} />
        : activeTab === 'whatsapp' ? renderWhatsappTab()
        : <ApiSettings />}
    </>
  );

  const renderEmailPreferencesTab = () => {
    const useMultipleFooters = emailSettings.useMultipleFooters === true;
    
    // Helper: get the footer currently being edited in master view
    const getEditFooter = () => {
      if (useMultipleFooters) {
        return footerList.find(f => f.id === editingFooterId) || null;
      }
      return emailSettings; // legacy single footer
    };

    // Helper: count active social icons for a footer
    const countSocialIcons = (footer) => {
      return (footer.socialIcons || []).filter(i => i.iconUrl).length;
    };

    // ── STEP 2: MASTER / EDIT VIEW ──
    const renderFooterEditView = () => {
      const ef = getEditFooter();
      if (!ef) return null;
      const isMulti = useMultipleFooters;
      const footerName = isMulti ? (ef.name || 'Untitled Footer') : 'Default Footer';

      // update helpers that work for both legacy & multi
      const updateField = (updates) => {
        if (isMulti) {
          handleUpdateFooter(ef.id, updates);
        } else {
          setEmailSettings(prev => ({ ...prev, ...updates }));
        }
      };

      return (
        <div className="footerEdit-master">
          {/* ── Header ── */}
          <div className="footerEdit-masterHeader">
            <button 
              type="button" 
              className="footerEdit-backBtn"
              onClick={() => { setFooterViewMode('overview'); setEditingFooterId(null); }}
            >
              <img src={downArrow} alt="back" className="footerEdit-backIcon" />
              <span>All Footers</span>
            </button>
            <div className="footerEdit-masterTitleRow">
              {isMulti ? (
                <input
                  type="text"
                  value={ef.name || ''}
                  onChange={(e) => handleUpdateFooter(ef.id, { name: e.target.value })}
                  className="footerEdit-masterTitleInput"
                  placeholder="Footer Name..."
                />
              ) : (
                <h3 className="footerEdit-masterTitle">Default Footer</h3>
              )}
              <button
                type="button"
                onClick={handleSaveFooterSettings}
                className="footerEdit-saveBtn"
                disabled={isSaved}
              >
                {isSaved ? '✓ Saved' : 'Save Footer'}
              </button>
            </div>
          </div>

          {/* ── Two-column layout ── */}
          <div className="footerEdit-masterGrid">
            {/* LEFT: Live preview */}
            <div className="footerEdit-previewCol">
              <div className="footerEdit-previewCard">
                <div className="footerEdit-previewLabel">
                  <span className="footerEdit-previewDot"></span>
                  Live Preview
                  <span className="footerEdit-fontBadge">{ef.footer_font || 'Helvetica Neue'}</span>
                </div>
                <div className="footerEdit-previewFrame-wrapper">
                  <iframe
                    title="Email Footer Preview"
                    className="footerEdit-previewFrame"
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{font-family:'${ef.footer_font || 'Helvetica Neue'}',Helvetica,Arial,sans-serif;padding:16px;background:#fff;margin:0;}a{color:#4a90e2;text-decoration:none;}a:hover{text-decoration:underline;}</style></head><body>${isMulti ? generateEmailFooter({ useMultipleFooters: true, footerList: [ef] }) : generateEmailFooter(emailSettings)}</body></html>`}
                    frameBorder="0"
                  />
                </div>
              </div>

              {/* Footer Spam Check */}
              <FooterDeliverabilityChecker footer={isMulti ? ef : emailSettings} />

              {/* Footer Text Editor */}
              <div className="footerEdit-textEditorCard">
                <label className="footerEdit-sectionLabel">Footer Text</label>
                <div
                  className="footerEdit-editorToolbar"
                  onMouseDownCapture={saveFooterEditorSelection}
                >
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => formatText('bold')} title="Bold"><strong>B</strong></button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => formatText('italic')} title="Italic"><em>I</em></button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => formatText('underline')} title="Underline"><u>U</u></button>
                  <select
                    title="Font size"
                    className="footerEdit-fontSizeSelect"
                    value=""
                    onChange={(e) => { if (e.target.value) formatText('fontSize', e.target.value); e.target.value = ''; }}
                  >
                    <option value="">Size</option>
                    <option value="1">10px</option>
                    <option value="2">12px</option>
                    <option value="3">14px</option>
                    <option value="4">16px</option>
                    <option value="5">18px</option>
                    <option value="6">24px</option>
                    <option value="7">32px</option>
                  </select>
                  <div className="footerEdit-colorPicker">
                    <input
                      ref={footerColorInputRef}
                      type="color"
                      className="footerEdit-colorInputHidden"
                      defaultValue="#000000"
                      onChange={(e) => {
                        const val = e.target.value;
                        e.target.value = '#000000';
                        formatText('foreColor', val);
                      }}
                      title="Text color"
                    />
                    <button
                      type="button"
                      title="Text color"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => footerColorInputRef.current?.click()}
                      className="footerEdit-colorTriggerBtn"
                      style={{ backgroundColor: '#333' }}
                    >
                      A
                    </button>
                  </div>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => formatText('createLink', prompt('Enter URL:'))} title="Insert Link">🔗</button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => formatText('removeFormat')} title="Remove Format">Clear</button>
                </div>
                <div
                  ref={editorRef}
                  className="footerEdit-htmlEditor"
                  contentEditable
                  style={{ fontFamily: `'${ef.footer_font || 'Helvetica Neue'}', Helvetica, Arial, sans-serif` }}
                  dangerouslySetInnerHTML={{ __html: isMulti ? (ef.footer_text || '') : emailSettings.footer_text }}
                  onBlur={(e) => updateField({ footer_text: e.target.innerHTML })}
                />
                <div className="footerEdit-htmlToggle">
                  <button type="button" onClick={() => setShowHtmlEditor(!showHtmlEditor)} className={showHtmlEditor ? 'active' : ''}>
                    {showHtmlEditor ? 'Close HTML' : 'Edit HTML'}
                  </button>
                </div>
                {showHtmlEditor && (
                  <textarea
                    className="footerEdit-htmlTextarea"
                    value={isMulti ? (ef.footer_text || '') : emailSettings.footer_text}
                    onChange={(e) => updateField({ footer_text: e.target.value })}
                    rows={6}
                    placeholder="Enter HTML code..."
                  />
                )}
              </div>
            </div>

            {/* RIGHT: Settings panels */}
            <div className="footerEdit-settingsCol">
              {/* Font */}
              <div className="footerEdit-panel">
                <label className="footerEdit-sectionLabel">Font</label>
                <select
                  value={ef.footer_font || "Helvetica Neue"}
                  onChange={(e) => updateField({ footer_font: e.target.value })}
                  className="footerEdit-select"
                >
                  <option value="Helvetica Neue">Helvetica Neue</option>
                  <option value="Arial">Arial</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Tahoma">Tahoma</option>
                  <option value="Times New Roman">Times New Roman</option>
                </select>
              </div>

              {/* Image */}
              <div className="footerEdit-panel">
                <label className="footerEdit-sectionLabel">Footer Image <span style={{fontWeight: 400, color: '#94a3b8', fontSize: '11px'}}>(optional)</span></label>
                <p className="footerEdit-imageHint">
                  Best deliverability for cold outreach comes from a text-only signature — a plain wordmark like your company name reaches the inbox more reliably than any logo. If you add an image it is embedded directly into the email (no external link) and automatically compressed to stay small, so it renders cleanly everywhere — but a logo is never required.
                </p>
                <div 
                  className="footerEdit-imageDropZone"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer?.files?.[0];
                    if (file) {
                      // Beide Pfade komprimieren jetzt automatisch + kappen bei 200 KB
                      // (handleFooterImageUpload / handleImageUpload).
                      if (isMulti) await handleFooterImageUpload(file, ef.id);
                      else await handleImageUpload({ target: { files: [file] } });
                    }
                  }}
                >
                  {(isMulti ? ef.footer_image_url : emailSettings.footer_image_url) ? (
                    <div className="footerEdit-imagePreview">
                      <img src={isMulti ? ef.footer_image_url : emailSettings.footer_image_url} alt="Footer" />
                      <button 
                        type="button"
                        className="footerEdit-removeImgBtn"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateField({ footer_image_url: "" }); }}
                      >✕</button>
                    </div>
                  ) : (
                    <div className="footerEdit-dropContent">
                      <span className="footerEdit-dropIcon">📷</span>
                      <span>Drop image or click to upload</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg, image/png, image/gif"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (isMulti) await handleFooterImageUpload(file, ef.id);
                        else await handleImageUpload(e);
                      }
                    }}
                    disabled={uploading}
                    className="footerEdit-fileInput"
                  />
                  {uploading && (
                    <div className="footerEdit-uploadOverlay">
                      <div className="footerEdit-spinner"></div>
                      Uploading...
                    </div>
                  )}
                </div>
                
                {/* Image Size */}
                <div className="footerEdit-subField">
                  <label>Size: {(isMulti ? ef.footer_image_size : emailSettings.footer_image_size) || 80}px</label>
                  <input
                    type="range" min="40" max="400"
                    value={(isMulti ? ef.footer_image_size : emailSettings.footer_image_size) || 80}
                    onChange={(e) => updateField({ footer_image_size: parseInt(e.target.value) })}
                    className="footerEdit-slider"
                  />
                </div>

                {/* Image Position */}
                <div className="footerEdit-subField">
                  <label>Position</label>
                  <div className="footerEdit-posToggle">
                    <button type="button"
                      className={`footerEdit-posBtn ${(isMulti ? ef.footer_image_position : emailSettings.footer_image_position) === 'below' ? 'active' : ''}`}
                      onClick={() => updateField({ footer_image_position: 'below' })}
                    >
                      <img src={downArrow} alt="below" className="footerEdit-posIcon" />
                      Below
                    </button>
                    <button type="button"
                      className={`footerEdit-posBtn ${(isMulti ? ef.footer_image_position : emailSettings.footer_image_position) === 'beside' ? 'active' : ''}`}
                      onClick={() => updateField({ footer_image_position: 'beside' })}
                    >
                      <img src={downArrow} alt="beside" className="footerEdit-posIcon rotateLeft" />
                      Beside
                    </button>
                  </div>
                </div>
              </div>

              {/* Social Icons */}
              <div className="footerEdit-panel">
                <div className="footerEdit-panelHeader" onClick={() => setShowSocialIcons(!showSocialIcons)}>
                  <label className="footerEdit-sectionLabel" style={{cursor:'pointer', margin:0}}>Social Media Icons</label>
                  <span className={`footerEdit-chevron ${showSocialIcons ? 'open' : ''}`}>▸</span>
                </div>
                {showSocialIcons && (
                  <div className="footerEdit-socialContent">
                    <div className="footerEdit-posToggle" style={{marginBottom: 12}}>
                      {['left','center','right'].map(pos => (
                        <button key={pos} type="button"
                          className={`footerEdit-posBtn ${(isMulti ? ef.socialIconsPosition : emailSettings.socialIconsPosition) === pos ? 'active' : ''}`}
                          onClick={() => updateField({ socialIconsPosition: pos })}
                        >{pos.charAt(0).toUpperCase() + pos.slice(1)}</button>
                      ))}
                    </div>
                    <div className="footerEdit-subField">
                      <label>Top Spacing: {(isMulti ? ef.socialIconsMarginTop : emailSettings.socialIconsMarginTop) ?? 15}px</label>
                      <input type="range" min="0" max="40"
                        value={(isMulti ? ef.socialIconsMarginTop : emailSettings.socialIconsMarginTop) ?? 15}
                        onChange={(e) => updateField({ socialIconsMarginTop: parseInt(e.target.value) })}
                        className="footerEdit-slider"
                      />
                    </div>
                    <div className="footerEdit-subField">
                      <label>Bottom Spacing: {(isMulti ? ef.socialIconsMarginBottom : emailSettings.socialIconsMarginBottom) ?? 10}px</label>
                      <input type="range" min="0" max="40"
                        value={(isMulti ? ef.socialIconsMarginBottom : emailSettings.socialIconsMarginBottom) ?? 10}
                        onChange={(e) => updateField({ socialIconsMarginBottom: parseInt(e.target.value) })}
                        className="footerEdit-slider"
                      />
                    </div>
                    <div className="footerEdit-iconGrid">
                      {((isMulti ? ef.socialIcons : emailSettings.socialIcons) || []).map((icon, index) => (
                        <div key={index} className="footerEdit-iconSlot">
                          <span className="footerEdit-iconNum">Icon {index + 1}</span>
                          <div className="footerEdit-iconUpload">
                            {icon.iconUrl ? (
                              <div className="footerEdit-iconThumb">
                                <img src={icon.iconUrl} alt={`Icon ${index + 1}`} />
                                <button type="button" className="footerEdit-iconRemove" onClick={() => {
                                  const icons = [...((isMulti ? ef.socialIcons : emailSettings.socialIcons) || [])];
                                  icons[index] = { iconUrl: "", link: "", size: 32 };
                                  updateField({ socialIcons: icons });
                                }}>✕</button>
                              </div>
                            ) : (
                              <label className="footerEdit-iconUploadLabel">
                                <span>📤 SVG</span>
                                <input type="file" accept="image/svg+xml"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      if (isMulti) await handleFooterSocialIconUpload(file, ef.id, index);
                                      else await handleSocialIconUpload(e, index);
                                    }
                                  }}
                                  disabled={uploading}
                                  className="footerEdit-fileInput"
                                />
                              </label>
                            )}
                          </div>
                          <input type="url" placeholder="Link URL" value={icon.link || ""}
                            onChange={(e) => {
                              const icons = [...((isMulti ? ef.socialIcons : emailSettings.socialIcons) || [])];
                              icons[index] = { ...icons[index], link: e.target.value };
                              updateField({ socialIcons: icons });
                            }}
                            className="footerEdit-iconLink"
                          />
                          <div className="footerEdit-subField" style={{marginTop: 4}}>
                            <label style={{fontSize: 11}}>Size: {icon.size || 32}px</label>
                            <input type="range" min="24" max="64" value={icon.size || 32}
                              onChange={(e) => {
                                const icons = [...((isMulti ? ef.socialIcons : emailSettings.socialIcons) || [])];
                                icons[index] = { ...icons[index], size: parseInt(e.target.value) };
                                updateField({ socialIcons: icons });
                              }}
                              className="footerEdit-slider"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Opt-Out Link */}
              <div className="footerEdit-panel">
                <div className="footerEdit-panelHeader" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                  <label className="footerEdit-sectionLabel" style={{margin: 0}}>Opt-Out Link</label>
                  <label className="footerEdit-toggle">
                    <input
                      type="checkbox"
                      checked={isMulti ? (ef.optOutEnabled || false) : (emailSettings.optOutEnabled || false)}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setConfirmModal({
                            open: true,
                            title: "Disable Opt-Out Link",
                            message: "By disabling the opt-out link, you take full responsibility for compliance with email marketing laws in your jurisdiction (e.g. GDPR, CAN-SPAM, CASL). This action will be logged. Are you sure you want to proceed?",
                            onConfirm: async () => {
                              setConfirmModal(prev => ({ ...prev, open: false }));
                              updateField({ optOutEnabled: false, optOutDisabledAt: Date.now() });
                              try {
                                const auditRef = collection(firestore, `World/${userId}/decisionAudit`);
                                await addDoc(auditRef, {
                                  type: 'optout_disabled',
                                  decision: 'user_disabled_optout',
                                  reasoning: 'User manually disabled opt-out link in footer settings',
                                  humanOverride: true,
                                  metadata: { footerId: isMulti ? ef.id : '__legacy__', userId },
                                  ts: Date.now(),
                                });
                              } catch (_) {}
                            }
                          });
                        } else {
                          updateField({ optOutEnabled: true });
                        }
                      }}
                    />
                    <span className="footerEdit-toggleSlider"></span>
                  </label>
                </div>
                <p className="footerEdit-optOutHint" style={{marginTop: 12, fontStyle: 'italic'}}>
                  Please check whether an opt-out link is legally required in your country. You are solely responsible for compliance with applicable email marketing laws (e.g. GDPR, CAN-SPAM). Disabling this will be logged.
                </p>
                {(isMulti ? ef.optOutEnabled : emailSettings.optOutEnabled) && (
                  <div className="footerEdit-optOutContent">
                    <p className="footerEdit-optOutHint">
                      A small unsubscribe link will appear below your footer. Recipients land on a neutral page with no branding.
                    </p>
                    <div className="footerEdit-subField">
                      <label>Link Text</label>
                      <input
                        type="text"
                        value={isMulti ? (ef.optOutText || '') : (emailSettings.optOutText || '')}
                        onChange={(e) => updateField({ optOutText: e.target.value })}
                        placeholder="Unsubscribe"
                        className="footerEdit-optOutInput"
                      />
                    </div>
                    <div className="footerEdit-optOutPreview">
                      <span style={{fontSize: 10, color: '#999', textDecoration: 'underline'}}>
                        {(isMulti ? ef.optOutText : emailSettings.optOutText) || 'Unsubscribe'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Wendet ein Template an: ersetzt den ersten leeren Footer, oder fügt einen
    // neuen ans Ende der footerList an. Anschließend springt die UI direkt
    // in den Edit-Modus auf den frischen Footer — der User passt einfach an.
    const handleApplyFooterTemplate = (templateId) => {
      const newFooter = buildFooterFromTemplate(templateId);
      if (!newFooter) return;
      let updatedList;
      let targetId;
      if (useMultipleFooters && footerList.length > 0) {
        const emptyIdx = footerList.findIndex((f) => !isFooterMeaningful(f));
        if (emptyIdx >= 0) {
          updatedList = [...footerList];
          // Behalte die existierende ID des leeren Slots — falls EmailAccounts
          // bereits dort hin verlinken (selectedFooterId), bleiben Refs intakt.
          const preservedId = updatedList[emptyIdx].id;
          updatedList[emptyIdx] = { ...newFooter, id: preservedId };
          targetId = preservedId;
        } else {
          updatedList = [...footerList, newFooter];
          targetId = newFooter.id;
        }
      } else {
        updatedList = [newFooter];
        targetId = newFooter.id;
      }
      setFooterList(updatedList);
      setSelectedFooterId(targetId);
      setEditingFooterId(targetId);
      setEmailSettings((prev) => ({
        ...prev,
        useMultipleFooters: true,
        footerList: updatedList,
      }));
      setFooterViewMode('edit');
    };

    // ── STEP 1: OVERVIEW CARDS ──
    const renderFooterOverview = () => {
      // Build a unified list of footers to show
      let allFooters = [];
      if (useMultipleFooters && footerList.length > 0) {
        allFooters = footerList;
      } else {
        // Legacy single footer – show as one card
        allFooters = [{
          id: '__legacy__',
          name: 'Default Footer',
          footer_text: emailSettings.footer_text || '',
          footer_image_url: emailSettings.footer_image_url || '',
          footer_image_position: emailSettings.footer_image_position || 'below',
          footer_font: emailSettings.footer_font || 'Helvetica Neue',
          footer_image_size: emailSettings.footer_image_size || 80,
          socialIcons: emailSettings.socialIcons || [],
          socialIconsPosition: emailSettings.socialIconsPosition || 'left',
          socialIconsMarginTop: emailSettings.socialIconsMarginTop ?? 15,
          socialIconsMarginBottom: emailSettings.socialIconsMarginBottom ?? 10,
          optOutEnabled: emailSettings.optOutEnabled || false,
          optOutText: emailSettings.optOutText || '',
          createdAt: null
        }];
      }

      // Empty state: kein einziger Footer hat echten Inhalt → Templates zeigen.
      const noContentYet = allFooters.every((f) => !isFooterMeaningful(f));

      return (
        <div className="footerOverview">
          {/* Header */}
          <div className="footerOverview-header">
            <div className="footerOverview-headerLeft">
              <h3 className="footerOverview-title">Email Footers</h3>
              <span className="footerOverview-count">{allFooters.length} Footer{allFooters.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="footerOverview-headerRight">
              {!useMultipleFooters && (
                <button type="button" onClick={handleMigrateToMultiFooter} className="footerOverview-migrateBtn">
                  Upgrade to Multi-Footer
                </button>
              )}
              {useMultipleFooters && (
                <button type="button" onClick={() => {
                  // Create the footer first, then switch to edit
                  const newId = `footer_${Date.now()}`;
                  const newFooter = {
                    id: newId,
                    name: `Footer ${footerList.length + 1}`,
                    footer_text: "",
                    footer_image_url: "",
                    footer_image_position: "below",
                    footer_font: "Helvetica Neue",
                    footer_image_size: 80,
                    socialIcons: [
                      { iconUrl: "", link: "", size: 32 },
                      { iconUrl: "", link: "", size: 32 },
                      { iconUrl: "", link: "", size: 32 },
                      { iconUrl: "", link: "", size: 32 }
                    ],
                    socialIconsPosition: "left",
                    socialIconsMarginTop: 15,
                    socialIconsMarginBottom: 10,
                    optOutEnabled: true,
                    optOutText: "Unsubscribe",
                    isActive: true,
                    createdAt: Date.now()
                  };
                  const updatedList = [...footerList, newFooter];
                  setFooterList(updatedList);
                  setSelectedFooterId(newId);
                  setEmailSettings(prev => ({ ...prev, footerList: updatedList }));
                  setEditingFooterId(newId);
                  setFooterViewMode('edit');
                }} className="footerOverview-addBtn">
                  + New Footer
                </button>
              )}
              <button type="button" onClick={handleSaveFooterSettings} className="footerOverview-saveBtn" disabled={isSaved}>
                {isSaved ? '✓ Saved' : 'Save'}
              </button>
            </div>
          </div>

          {/* Empty state — show template slideshow instead of an empty card */}
          {noContentYet ? (
            <div className="footerOverview-templates">
              <div className="footerOverview-templatesIntro">
                <h4 className="footerOverview-templatesTitle">Start with a template</h4>
                <p className="footerOverview-templatesSubtitle">
                  Pick one of these starters — we'll create a footer with sample data.
                  Click and adjust the details to match your brand.
                </p>
              </div>
              <div className="footerOverview-templatesScroll" role="list">
                {FOOTER_TEMPLATES.map((tpl) => {
                  const previewHtml = generateEmailFooter({
                    useMultipleFooters: true,
                    footerList: [{ ...tpl.footer, id: `tpl_${tpl.id}`, isActive: true }],
                  });
                  return (
                    <button
                      type="button"
                      role="listitem"
                      key={tpl.id}
                      className="footerOverview-templateCard"
                      onClick={() => handleApplyFooterTemplate(tpl.id)}
                      style={{ ['--tpl-accent']: tpl.accent }}
                    >
                      <div className="footerOverview-templateCardPreview">
                        <iframe
                          title={`Template ${tpl.label}`}
                          className="footerOverview-cardFrame"
                          srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:'Jost',Helvetica,Arial,sans-serif;padding:10px;background:#fff;margin:0;transform:scale(0.85);transform-origin:top left;}a{color:#4a90e2;text-decoration:none;}</style></head><body>${previewHtml}</body></html>`}
                          frameBorder="0"
                          scrolling="no"
                          tabIndex={-1}
                          style={{ pointerEvents: 'none' }}
                        />
                      </div>
                      <div className="footerOverview-templateCardBody">
                        <div className="footerOverview-templateCardName">{tpl.label}</div>
                        <div className="footerOverview-templateCardDesc">{tpl.description}</div>
                      </div>
                      <div className="footerOverview-templateCardCta">
                        Use this template <span aria-hidden>→</span>
                      </div>
                    </button>
                  );
                })}
                <div className="footerOverview-templateCard footerOverview-templateCardScratch" onClick={() => {
                  if (!useMultipleFooters) {
                    handleMigrateToMultiFooter();
                    return;
                  }
                  const targetId = footerList[0]?.id;
                  if (targetId) {
                    setEditingFooterId(targetId);
                    setSelectedFooterId(targetId);
                    setFooterViewMode('edit');
                  }
                }}>
                  <div className="footerOverview-templateCardScratchInner">
                    <div className="footerOverview-templateCardScratchIcon">+</div>
                    <div className="footerOverview-templateCardName">Start from scratch</div>
                    <div className="footerOverview-templateCardDesc">Build your own footer from a blank slate.</div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Cards Grid — only when at least one footer has real content */}
          <div className="footerOverview-grid" style={noContentYet ? { display: 'none' } : undefined}>
            {allFooters.map((footer) => {
              const hasImage = !!footer.footer_image_url;
              const iconCount = countSocialIcons(footer);
              const hasText = !!footer.footer_text && footer.footer_text.replace(/<[^>]*>/g,'').trim().length > 0;
              const plainText = (footer.footer_text || '').replace(/<[^>]*>/g,'').trim();
              const footerSpam = analyzeFooter(useMultipleFooters ? footer : emailSettings);
              const spamScore = footerSpam ? footerSpam.score : null;
              const spamTier = spamScore == null ? '' : spamScore >= 7 ? 'good' : spamScore >= 4 ? 'warn' : 'bad';
              const spamWord = spamScore == null ? '' : spamScore >= 7 ? 'No Spam' : spamScore >= 4 ? 'Slightly Spammy' : 'Likely Spam';

              return (
                <div 
                  key={footer.id} 
                  className="footerOverview-card"
                  onClick={() => {
                    if (useMultipleFooters) {
                      setEditingFooterId(footer.id);
                      setSelectedFooterId(footer.id);
                    } else {
                      setEditingFooterId('__legacy__');
                    }
                    setFooterViewMode('edit');
                  }}
                >
                  {/* Mini Preview */}
                  <div className="footerOverview-cardPreview">
                    <iframe
                      title={`Preview ${footer.name || 'Footer'}`}
                      className="footerOverview-cardFrame"
                      srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{font-family:'Jost',Helvetica,Arial,sans-serif;padding:8px;background:#fff;margin:0;transform:scale(0.85);transform-origin:top left;}a{color:#4a90e2;text-decoration:none;}</style></head><body>${useMultipleFooters ? generateEmailFooter({ useMultipleFooters: true, footerList: [footer] }) : generateEmailFooter(emailSettings)}</body></html>`}
                      frameBorder="0"
                      scrolling="no"
                      tabIndex={-1}
                      style={{pointerEvents: 'none'}}
                    />
                  </div>

                  {/* Card Info */}
                  <div className="footerOverview-cardBody">
                    <div className="footerOverview-cardName">{footer.name || 'Untitled Footer'}</div>
                    <div className="footerOverview-cardMeta">
                      <span className="footerOverview-metaTag">{footer.footer_font || 'Helvetica Neue'}</span>
                      {hasImage && <span className="footerOverview-metaTag">Image</span>}
                      {iconCount > 0 && <span className="footerOverview-metaTag">{iconCount} Icon{iconCount !== 1 ? 's' : ''}</span>}
                      {hasText && <span className="footerOverview-metaTag footerOverview-metaText" title={plainText}>Text</span>}
                      {spamScore != null && (
                        <span
                          className={`footerOverview-spamTag footerOverview-spamTag--${spamTier}`}
                          title={`Footer spam check: ${spamScore}/10 — ${spamWord}`}
                        >
                          <span className="footerOverview-spamDot" />
                          {spamWord} · {spamScore}/10
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card actions */}
                  <div className="footerOverview-cardActions">
                    <button type="button" className="footerOverview-editBtn" onClick={(e) => {
                      e.stopPropagation();
                      if (useMultipleFooters) {
                        setEditingFooterId(footer.id);
                        setSelectedFooterId(footer.id);
                      } else {
                        setEditingFooterId('__legacy__');
                      }
                      setFooterViewMode('edit');
                    }}>
                      Edit
                    </button>
                    {useMultipleFooters && (
                      <button type="button" className="footerOverview-duplicateBtn" onClick={(e) => {
                        e.stopPropagation();
                        const newId = `footer_${Date.now()}`;
                        const duplicated = {
                          ...JSON.parse(JSON.stringify(footer)),
                          id: newId,
                          name: `${footer.name || 'Footer'} (Copy)`,
                          createdAt: Date.now()
                        };
                        const updatedList = [...footerList, duplicated];
                        setFooterList(updatedList);
                        setEmailSettings(prev => ({ ...prev, footerList: updatedList }));
                      }}>
                        Duplicate
                      </button>
                    )}
                    {useMultipleFooters && footerList.length > 1 && (
                      <button type="button" className="footerOverview-deleteBtn" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFooter(footer.id);
                      }}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    // ── RENDER DECISION ──
    return (
      <div className="emailFooterSettingsSection">
        {footerViewMode === 'edit' ? renderFooterEditView() : renderFooterOverview()}
      </div>
    );
  };

  // Der angezeigte Zustand folgt immer dem, was der Nutzer selbst sieht: eine
  // vorhandene Verbindung schlägt einen veralteten Backend-Status. Nur der
  // interne Kill-Switch steht über allem.
  const describeInboxState = () => {
    const backendState = inboxStatus?.state;
    const accounts = inboxAccountCount ?? inboxStatus?.accounts ?? 0;

    if (inboxAccountCount === null && !inboxStatus) {
      return { tone: 'off', label: '…', text: 'Checking mailbox connections.' };
    }
    if (backendState === 'disabled') {
      return {
        tone: 'off',
        label: 'Paused',
        text: 'Email retrieval was paused by BonusClub. Contact support to switch it back on.',
      };
    }
    if (accounts === 0) {
      return {
        tone: 'off',
        label: 'No account',
        text: 'Connect a mailbox under Email → Accounts — retrieval starts on its own from there.',
      };
    }
    if (backendState === 'idle') {
      return {
        tone: 'idle',
        label: 'Sleeping',
        text: 'Retrieval was paused after a long time without usage. It resumes the next time you open the inbox.',
      };
    }
    return {
      tone: 'on',
      label: 'Active',
      text: `Mail from ${accounts} connected ${accounts === 1 ? 'mailbox' : 'mailboxes'} is retrieved automatically.`,
    };
  };

  const renderAdvancedTab = () => {
    const inbox = describeInboxState();
    const guidelinesOpen = expandedAiGuideline === 'reply';
    return (
      <div className="as">
        <header className="as-page-head">
          <div>
            <h3 className="as-page-title">Advanced Settings</h3>
            <p className="as-page-sub">
              Inbox retrieval, reply drafts, and the automations that run in the background.
            </p>
          </div>
        </header>

        <section className="as-section">
          <header>
            <span className="as-section-eyebrow">Inbox</span>
            <h4 className="as-section-title">Reply guidelines</h4>
            <p className="as-section-sub">
              Rules the reply agent follows when it writes back to incoming mail.
            </p>
          </header>

          <div className={`as-perm ${guidelinesOpen ? 'is-open' : ''}`}>
            <button
              type="button"
              className="as-perm-head"
              aria-expanded={guidelinesOpen}
              onClick={() => toggleAiGuidelineExpansion('reply')}
            >
              <span className="as-icon" aria-hidden>
                <i className="fa fa-inbox" />
              </span>
              <span className="as-perm-copy">
                <span className="as-perm-title">Inbox-Reply Guidelines</span>
                <span className="as-perm-desc">Rules for responding to incoming emails</span>
              </span>
              <i className="fa fa-chevron-down as-perm-chevron" aria-hidden />
            </button>
            {guidelinesOpen && (
              <div className="as-perm-body">
                <textarea
                  name="replyAgentGuidelines"
                  value={aiSettings.replyAgentGuidelines || ""}
                  onChange={handleAiChange}
                  className="as-textarea"
                  placeholder="Add specific instructions for reply generation, starting each with '- '..."
                  rows="8"
                />
                <div className="as-perm-actions">
                  <span className="as-char-count">
                    {getCharacterCount(aiSettings.replyAgentGuidelines)} characters
                  </span>
                  <button
                    type="button"
                    onClick={() => resetReplyAgentGuidelines()}
                    className="altBtn altBtn--petrol"
                  >
                    Reset to default
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="as-section">
          <header>
            <span className="as-section-eyebrow">Automation</span>
            <h4 className="as-section-title">What runs on its own</h4>
            <p className="as-section-sub">
              Drafts, blocking, and bounce handling — switch only what you want running.
            </p>
          </header>

          <div className="as-rows">
            <div className="as-row">
              <div className="as-row-head">
                <span className="as-icon" aria-hidden>
                  <i className="fa fa-envelope" />
                </span>
                <div className="as-row-copy">
                  <h5 className="as-row-title">Smart Inbox</h5>
                  <p className="as-row-desc">{inbox.text}</p>
                </div>
                <span className={`as-state ${inbox.tone}`}>{inbox.label}</span>
              </div>
            </div>

            <div className="as-row">
              <div className="as-row-head">
                <span className="as-icon" aria-hidden>
                  <i className="fa fa-wand-magic-sparkles" />
                </span>
                <div className="as-row-copy">
                  <h5 className="as-row-title">Auto Generate Email-Replies</h5>
                  <p className="as-row-desc">
                    {aiSettings.autoProcessEmails
                      ? "Agents automatically create drafts for new emails based on enthusiasm level"
                      : "Enable automatic draft creation by AI agents for incoming emails"}
                  </p>
                </div>
                <button
                  type="button"
                  className={`as-toggle ${aiSettings.autoProcessEmails ? 'active' : ''}`}
                  onClick={() => setAiSettings((prev) => ({
                    ...prev,
                    autoProcessEmails: !prev.autoProcessEmails,
                  }))}
                  aria-pressed={!!aiSettings.autoProcessEmails}
                  aria-label="Auto generate email replies"
                >
                  <span className="as-toggle-knob" />
                </button>
              </div>
            </div>

            <div className="as-row">
              <div className="as-row-head">
                <span className="as-icon" aria-hidden>
                  <i className="fa fa-ban" />
                </span>
                <div className="as-row-copy">
                  <h5 className="as-row-title">Auto-Block Negative Replies</h5>
                  <p className="as-row-desc">
                    {aiSettings.autoBlockNegativeReplies
                      ? `Replies rated ${aiSettings.autoBlockMaxScore ?? 2}/10 or lower (with subtype no_need / hostile / spam_accusation) are automatically blacklisted.`
                      : "Strongly negative replies will not be auto-blacklisted. You can still block manually from the Smart Inbox."}
                  </p>
                </div>
                <button
                  type="button"
                  className={`as-toggle ${aiSettings.autoBlockNegativeReplies ? 'active' : ''}`}
                  onClick={() => setAiSettings((prev) => ({
                    ...prev,
                    autoBlockNegativeReplies: !prev.autoBlockNegativeReplies,
                  }))}
                  aria-pressed={!!aiSettings.autoBlockNegativeReplies}
                  aria-label="Auto-block negative replies"
                >
                  <span className="as-toggle-knob" />
                </button>
              </div>

              {aiSettings.autoBlockNegativeReplies && (
                <div className="as-threshold">
                  <label className="as-threshold-label">
                    Block when score is at most
                    <strong className="as-threshold-value">
                      {aiSettings.autoBlockMaxScore ?? 2} / 10
                    </strong>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={aiSettings.autoBlockMaxScore ?? 2}
                    onChange={(e) => setAiSettings((prev) => ({
                      ...prev,
                      autoBlockMaxScore: parseInt(e.target.value, 10),
                    }))}
                    className="as-slider"
                  />
                  <div className="as-slider-scale">
                    <span>0 — very negative</span>
                    <span>5 — neutral</span>
                    <span>10 — enthusiastic</span>
                  </div>
                  <p className="as-threshold-hint">
                    Custom-domain senders (e.g. <code>@firma.de</code>) get the entire domain blocked.
                    Consumer providers (Gmail, GMX, …) only get the individual address blocked.
                  </p>
                </div>
              )}
            </div>

            <div className="as-row">
              <div className="as-row-head">
                <span className="as-icon" aria-hidden>
                  <i className="fa fa-shield-halved" />
                </span>
                <div className="as-row-copy">
                  <h5 className="as-row-title">Activate Bounce Reduction</h5>
                  <p className="as-row-desc">
                    {aiSettings.bounceReduction
                      ? "Just before sending, decision-maker addresses are re-checked. If one is invalid or high-risk, the email is switched to the company standard mailbox (info@, kontakt@, …). If none exists, it is parked in SafeGuard as a finally-invalid address — no bounce."
                      : "When off, emails are sent to the resolved recipient with only the standard pre-send check. Enable to re-verify decision-maker addresses and auto-switch risky ones to the company standard mailbox."}
                  </p>
                </div>
                <button
                  type="button"
                  className={`as-toggle ${aiSettings.bounceReduction ? 'active' : ''}`}
                  onClick={() => setAiSettings((prev) => ({
                    ...prev,
                    bounceReduction: !prev.bounceReduction,
                  }))}
                  aria-pressed={!!aiSettings.bounceReduction}
                  aria-label="Activate bounce reduction"
                >
                  <span className="as-toggle-knob" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Scanne die DB nach Einträgen, die keiner bekannten Import-Liste zugewiesen sind
  const handleScanOrphans = async () => {
    if (!userId) return;
    setIsScanningOrphans(true);
    setOrphanedImports([]);
    setOrphanScanDone(false);

    try {
      // 1. Alle bekannten Import-Namen laden
      const knownImportNames = new Set(imports);

      // 2. ALLE Docs aus der DB laden und nach import_name gruppieren
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const allDocsSnapshot = await getDocs(dbCollectionRef);
      const totalDbCount = allDocsSnapshot.size;

      const importNameCounts = {};
      let noImportNameCount = 0;

      allDocsSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        const importName = data.import_name;
        
        if (!importName || (typeof importName === 'string' && importName.trim() === '')) {
          // Kein import_name → "unassigned"
          noImportNameCount++;
        } else {
          importNameCounts[importName] = (importNameCounts[importName] || 0) + 1;
        }
      });

      // 3. Finde ALLE nicht-zugewiesenen Einträge:
      //    - Einträge deren import_name NICHT in der import_names Collection existiert
      //    - Einträge ohne import_name
      const orphans = [];

      // 3a. Einträge mit unbekanntem import_name
      for (const [name, count] of Object.entries(importNameCounts)) {
        if (!knownImportNames.has(name)) {
          orphans.push({ name, count, type: 'unknown_import' });
        }
      }

      // 3b. Einträge ohne import_name
      if (noImportNameCount > 0) {
        orphans.push({ name: '(No import name)', count: noImportNameCount, type: 'no_import_name' });
      }

      // Sortiere nach Anzahl (größte zuerst)
      orphans.sort((a, b) => b.count - a.count);

      // Berechne Zusammenfassung
      const knownTotal = Object.entries(importNameCounts)
        .filter(([name]) => knownImportNames.has(name))
        .reduce((sum, [, count]) => sum + count, 0);
      const orphanTotal = orphans.reduce((sum, o) => sum + o.count, 0);

      setOrphanedImports(orphans);
      setOrphanScanDone(true);

      // Speichere Scan-Zusammenfassung im State für die UI
      setOrphanScanSummary({ totalDbCount, knownTotal, orphanTotal });

    } catch (error) {
      console.error('Error scanning for orphaned imports:', error);
      setMessage(`Error scanning: ${error.message}`);
    } finally {
      setIsScanningOrphans(false);
    }
  };

  // Lösche einen Geister-Import aus der DB (unterstützt auch Einträge ohne import_name)
  const handleDeleteOrphan = (orphanName, orphanType) => {
    if (!userId) return;
    const orphanEntry = orphanedImports.find(o => o.name === orphanName);
    const entryCount = orphanEntry?.count || 0;

    setConfirmModal({
      open: true,
      title: "Delete Orphaned Entries",
      message: orphanType === 'no_import_name'
        ? `Delete all ${entryCount.toLocaleString('de-DE')} entries WITHOUT import name? This cannot be undone.`
        : `Delete all ${entryCount.toLocaleString('de-DE')} entries from orphaned import "${orphanName}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setIsDeletingOrphan(true);
        const label = orphanType === 'no_import_name' ? 'entries without import name' : `"${orphanName}"`;
        setOrphanDeleteProgress(`Deleting ${label}...`);

        try {
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      
      // Für Einträge ohne import_name: alle Docs laden und filtern
      // Für bekannte orphan imports: query mit where
      let docsToDelete = [];
      
      if (orphanType === 'no_import_name') {
        // Firestore kann nicht direkt nach "field doesn't exist" oder "field is empty" querien
        // Wir müssen alle Docs laden und client-seitig filtern
        const allDocs = await getDocs(dbCollectionRef);
        allDocs.forEach(docSnap => {
          const importName = docSnap.data().import_name;
          if (!importName || (typeof importName === 'string' && importName.trim() === '')) {
            docsToDelete.push(docSnap);
          }
        });
      } else {
        const q = query(dbCollectionRef, where("import_name", "==", orphanName));
        const snapshot = await getDocs(q);
        docsToDelete = snapshot.docs;
      }

      const totalCount = docsToDelete.length;

      if (totalCount === 0) {
        setOrphanDeleteProgress(`No entries found for ${label}.`);
        setOrphanedImports(prev => prev.filter(o => o.name !== orphanName));
        setIsDeletingOrphan(false);
        return;
      }

      const BATCH_SIZE = 200;
      let deletedCount = 0;

      for (let i = 0; i < docsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        const chunk = docsToDelete.slice(i, i + BATCH_SIZE);
        chunk.forEach(d => batch.delete(d.ref));
        await batch.commit();
        deletedCount += chunk.length;

        const progress = Math.round((deletedCount / totalCount) * 100);
        setOrphanDeleteProgress(`Deleting ${label} – ${progress}% (${deletedCount.toLocaleString('de-DE')}/${totalCount.toLocaleString('de-DE')})`);

        // Pause zwischen Batches
        if (i + BATCH_SIZE < docsToDelete.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Auch das import_names Dokument löschen, falls es doch existiert
      if (orphanType !== 'no_import_name') {
        try {
          const importNameDocRef = doc(firestore, `World/${userId}/import_names`, orphanName);
          await deleteDoc(importNameDocRef);
        } catch (_) { /* Ignorieren wenn nicht vorhanden */ }
      }

      setOrphanDeleteProgress(`✅ ${label} deleted – ${deletedCount.toLocaleString('de-DE')} entries removed.`);
      setOrphanedImports(prev => prev.filter(o => o.name !== orphanName));
      // Summary aktualisieren
      setOrphanScanSummary(prev => prev ? {
        ...prev,
        totalDbCount: prev.totalDbCount - deletedCount,
        orphanTotal: prev.orphanTotal - deletedCount
      } : null);

      // Nachricht nach 5 Sekunden ausblenden
      setTimeout(() => setOrphanDeleteProgress(''), 5000);
    } catch (error) {
      console.error('Error deleting orphaned import:', error);
          setOrphanDeleteProgress(`❌ Error: ${error.message}`);
        } finally {
          setIsDeletingOrphan(false);
        }
      }
    });
  };

  // Funktion zum Löschen eines Imports (Firestore) - Optimiert für große Listen mit Batch-Delete
  const handleDeleteImport = async () => {
    if (!selectedImport || deleteConfirmText !== selectedImport) {
      setMessage("Please confirm the import name correctly to proceed.");
      return;
    }

    try {
      setIsDeleting(true);
      setMessage(`Deleting import "${selectedImport}" - please wait...`);

      // Alle zu löschenden Dokumente laden
      const dbCollectionRef = collection(firestore, `World/${userId}/db`);
      const q = query(dbCollectionRef, where("import_name", "==", selectedImport));
      const querySnapshot = await getDocs(q);
      const docsToDelete = querySnapshot.docs;
      const totalCount = docsToDelete.length;

      if (totalCount === 0) {
        setMessage(`No entries found for import "${selectedImport}".`);
        setIsDeleting(false);
        return;
      }

      // Batch-Größe: kleiner für Stabilität bei großen Listen
      const BATCH_SIZE = 200;
      let deletedCount = 0;

      // Aufteilen in Chunks und in Batches löschen
      for (let i = 0; i < docsToDelete.length; i += BATCH_SIZE) {
        const batch = writeBatch(firestore);
        const chunk = docsToDelete.slice(i, i + BATCH_SIZE);
        
        chunk.forEach((d) => {
          batch.delete(d.ref);
        });

        // Batch committen
        await batch.commit();
        deletedCount += chunk.length;

        // Progress-Update für große Listen
        const progress = Math.round((deletedCount / totalCount) * 100);
        setMessage(`Deleting import "${selectedImport}" – ${progress}% (${deletedCount.toLocaleString('de-DE')}/${totalCount.toLocaleString('de-DE')} entries)`);
        
        // Pause zwischen Batches, um Write-Stream nicht zu überlasten
        if (i + BATCH_SIZE < docsToDelete.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Import-Name-Dokument separat löschen (nicht in Batch, da anderer Collection-Pfad)
      const importNameDocRef = doc(firestore, `World/${userId}/import_names`, selectedImport);
      await deleteDoc(importNameDocRef);

      setMessage(`Import "${selectedImport}" successfully deleted. ${deletedCount} entries were removed.`);

      // Lokalen State aktualisieren
      setImports(prev => prev.filter(imp => imp !== selectedImport));
      setImportMetadata(prev => prev.filter(imp => imp.id !== selectedImport));
      setSelectedImport('');
      setDeleteConfirmText('');
      setImportCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[selectedImport];
        return newCounts;
      });

    } catch (error) {
      console.error("Error deleting import from Firestore:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Neue Funktion für den WhatsApp Tab
  const renderWhatsappTab = () => (
    <WhatsappSettings userId={userId} />
  );

  // Neue Funktion für den Whisper Tab
  const renderWhisperTab = () => (
    <WhisperSettings userId={userId} />
  );

  // Funktion zum Zurücksetzen des E-Mail-Scheduling
  const handleResetEmailScheduling = async () => {
    if (!userId) return;
    
    setIsResettingScheduling(true);
    setSchedulingResetSuccess(false);
    
    try {
      // 1. Lösche alle Einträge in lastEmailSent (für normale E-Mails)
      const lastEmailSentRef = ref(database, `/World/${userId}/lastEmailSent`);
      await remove(lastEmailSentRef);
      console.log('✅ lastEmailSent successfully reset');
      
      // 2. Lösche alle Einträge in lastReplySent (für Antworten)
      const lastReplySentRef = ref(database, `/World/${userId}/lastReplySent`);
      await remove(lastReplySentRef);
      console.log('✅ lastReplySent successfully reset');
      
      // 3. Optional: Lösche geplante E-Mails (scheduledEmails)
      // Diese werden nicht gelöscht, da sie bereits in der Warteschlange sind
      // und trotzdem gesendet werden sollten
      
      setSchedulingResetSuccess(true);
      setMessage('Email scheduling successfully reset! New emails will be scheduled without considering old ones.');
      
      // Reset-Erfolgsmeldung nach 5 Sekunden ausblenden
      setTimeout(() => {
        setSchedulingResetSuccess(false);
        setMessage('');
      }, 5000);
      
    } catch (error) {
      console.error('Error resetting email scheduling:', error);
      setMessage('Error resetting email scheduling: ' + error.message);
    } finally {
      setIsResettingScheduling(false);
    }
  };

  // Neue Funktion für den Danger Zone Tab
  const renderDangerZoneTab = () => (
    <div className="settingsGroup dangerZone">
      <h3>Danger Zone</h3>
      <div className="dangerZoneWarning">
        <span className="warningIcon">⚠️</span>
        <p>
          Warning: Actions in this area cannot be undone.
          Please be careful as deleted data cannot be recovered.
        </p>
      </div>

      {/* === User ID === */}
      <div className="dangerZoneSection">
        <h4>🆔 User ID</h4>
        <p>Your internal account identifier. Keep this handy for support requests.</p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            color: '#334155',
            wordBreak: 'break-all',
          }}
        >
          <span style={{ flex: 1 }}>{userId || '—'}</span>
          {userId && (
            <button
              type="button"
              className="altBtn altBtn--petrol"
              onClick={() => {
                navigator.clipboard?.writeText(userId);
                setMessage('User ID copied to clipboard');
                setTimeout(() => setMessage(''), 2500);
              }}
            >
              Copy
            </button>
          )}
        </div>
      </div>

      {/* === Import History === */}
      <div className="dangerZoneSection">
        <h4>📋 Import History</h4>
        <p>Overview of your last imports with current entry counts.</p>
        
        {importMetadata.length > 0 ? (
          <div className="importHistoryTable">
            <table>
              <thead>
                <tr>
                  <th>Import Name</th>
                  <th>Date</th>
                  <th>Imported</th>
                  <th>Current</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {importMetadata.slice(0, 10).map((imp, idx) => {
                  const currentCount = importCounts[imp.id] ?? '…';
                  const importedCount = imp.imported_count;
                  const deletedSince = importedCount != null && typeof currentCount === 'number'
                    ? importedCount - currentCount
                    : null;
                  const isInProgress = imp.status === 'in_progress';
                  
                  return (
                    <tr key={imp.id} className={idx % 2 === 0 ? 'evenRow' : 'oddRow'}>
                      <td className="importHistoryName" title={imp.name}>
                        {imp.name}
                      </td>
                      <td className="importHistoryDate">
                        {imp.last_imported 
                          ? new Date(imp.last_imported).toLocaleString('de-DE', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })
                          : '–'
                        }
                      </td>
                      <td className="importHistoryCount">
                        {importedCount != null ? importedCount.toLocaleString('de-DE') : '–'}
                      </td>
                      <td className="importHistoryCount">
                        <span className={currentCount === 0 ? 'countZero' : ''}>
                          {typeof currentCount === 'number' ? currentCount.toLocaleString('de-DE') : currentCount}
                        </span>
                        {deletedSince > 0 && (
                          <span className="deletedBadge" title={`${deletedSince} entries deleted since import`}>
                            −{deletedSince}
                          </span>
                        )}
                      </td>
                      <td className="importHistoryStatus">
                        {isInProgress 
                          ? <span className="importStatusBadge importStatusProgress">⚠ Incomplete</span>
                          : <span className="importStatusBadge importStatusComplete">✓ Complete</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {importMetadata.length > 10 && (
              <div className="importHistoryMore">
                + {importMetadata.length - 10} more imports
              </div>
            )}
          </div>
        ) : (
          <p className="noImportsMessage">No imports found yet.</p>
        )}
      </div>

      {/* === Orphan Scanner – Nicht zugewiesene Einträge finden === */}
      <div className="dangerZoneSection">
        <h4>🔍 Find Unassigned Entries</h4>
        <p>
          Scans your entire database and compares it with your known import lists.
          Finds entries that were partially imported (e.g. due to a crash) or have no 
          import name assigned.
        </p>

        <button
          className="orphanScanButton"
          onClick={handleScanOrphans}
          disabled={isScanningOrphans || isDeletingOrphan}
        >
          {isScanningOrphans ? (
            <><span className="orphanSpinner"></span> Scanning entire database...</>
          ) : (
            <><span>🔍</span> Scan database for unassigned entries</>
          )}
        </button>

        {orphanDeleteProgress && (
          <div className="orphanProgressMessage">{orphanDeleteProgress}</div>
        )}

        {/* Summary Box nach Scan */}
        {orphanScanDone && orphanScanSummary && (
          <div className="orphanSummaryBox">
            <div className="orphanSummaryRow">
              <span>📊 Total entries in database:</span>
              <strong>{orphanScanSummary.totalDbCount.toLocaleString('de-DE')}</strong>
            </div>
            <div className="orphanSummaryRow">
              <span>✅ Assigned to known imports:</span>
              <strong style={{ color: '#059669' }}>{orphanScanSummary.knownTotal.toLocaleString('de-DE')}</strong>
            </div>
            <div className="orphanSummaryRow orphanSummaryHighlight">
              <span>⚠️ Not assigned / unknown:</span>
              <strong style={{ color: orphanScanSummary.orphanTotal > 0 ? '#dc2626' : '#059669' }}>
                {orphanScanSummary.orphanTotal.toLocaleString('de-DE')}
              </strong>
            </div>
          </div>
        )}

        {orphanScanDone && orphanedImports.length > 0 && (
          <div className="orphanResultsContainer">
            <div className="orphanWarningBanner">
              ⚠️ Found <strong>{orphanedImports.length}</strong> group(s) of unassigned entries 
              with <strong>{orphanedImports.reduce((sum, o) => sum + o.count, 0).toLocaleString('de-DE')}</strong> total entries
            </div>
            <table className="orphanTable">
              <thead>
                <tr>
                  <th>Import Name</th>
                  <th>Type</th>
                  <th>Entries</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orphanedImports.map(orphan => (
                  <tr key={orphan.name}>
                    <td className="orphanName" title={orphan.name}>
                      {orphan.type === 'no_import_name' 
                        ? <em style={{ color: '#9ca3af' }}>(No import name)</em>
                        : orphan.name
                      }
                    </td>
                    <td className="orphanType">
                      {orphan.type === 'no_import_name' 
                        ? <span className="orphanTypeBadge orphanTypeMissing">Missing</span>
                        : <span className="orphanTypeBadge orphanTypeGhost">Ghost Import</span>
                      }
                    </td>
                    <td className="orphanCount">{orphan.count.toLocaleString('de-DE')}</td>
                    <td>
                      <button
                        className="orphanDeleteButton"
                        onClick={() => handleDeleteOrphan(orphan.name, orphan.type)}
                        disabled={isDeletingOrphan}
                      >
                        {isDeletingOrphan ? '...' : '🗑️ Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {orphanScanDone && orphanedImports.length === 0 && (
          <div className="orphanSuccessMessage">
            ✅ All clear – all entries are assigned to known imports.
          </div>
        )}
      </div>
      
      <div className="dangerZoneSection">
        <h4>Delete Import Data</h4>
        <p>
          Here you can remove all entries of a specific import from the database.
          This affects all records marked with the selected import ID.
        </p>
        
        {imports.length > 0 ? (
          <div className="deleteImportContainer">
            <div className="settingField">
              <label>Select import:</label>
              <select
                value={selectedImport}
                onChange={(e) => {
                  setSelectedImport(e.target.value);
                  setDeleteConfirmText('');
                }}
                className="importSelect"
              >
                <option value="">-- Please select --</option>
                {imports.map(imp => (
                  <option key={imp} value={imp}>
                    {imp} ({importCounts[imp] || 0} entries)
                  </option>
                ))}
              </select>
            </div>
            
            {selectedImport && (
              <div className="deleteConfirmation">
                <div className="settingField">
                  <label>Enter "{selectedImport}" to confirm:</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={`Enter "${selectedImport}"`}
                    className="deleteConfirmInput"
                  />
                </div>
                
                <button
                  className="deleteImportButton"
                  onClick={handleDeleteImport}
                  disabled={isDeleting || deleteConfirmText !== selectedImport}
                >
                  {isDeleting ? "Deleting..." : `Delete import (${importCounts[selectedImport] || 0} entries)`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="noImportsMessage">No imports found.</p>
        )}
      </div>
      
      {/* Email Scheduling Reset Section */}
      <div className="dangerZoneSection" style={{ marginTop: '30px' }}>
        <h4>🔄 Reset Email Scheduling Queue</h4>
        <p>
          This resets the email scheduling system. If you have emails scheduled days in advance, 
          this will make the system "forget" those timestamps. New emails will be scheduled 
          starting fresh, as if no previous emails were sent.
        </p>
        
        {/* Current Scheduling Status Display */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px',
            fontWeight: '600',
            color: '#6366f1'
          }}>
            <span style={{ fontSize: '16px' }}>📊</span>
            <span>Current Scheduling Status</span>
          </div>
          
          {schedulingStatus.isLoading ? (
            <div style={{ color: '#888', fontSize: '13px' }}>Loading status...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Last Email Sent */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📧</span>
                  <span style={{ fontWeight: '500' }}>Next Email Available:</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {schedulingStatus.lastEmailSent ? (
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        color: schedulingStatus.lastEmailSent.timestamp > Date.now() ? '#f59e0b' : '#10b981'
                      }}>
                        {new Date(schedulingStatus.lastEmailSent.timestamp).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      {schedulingStatus.lastEmailSent.timestamp > Date.now() && (
                        <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>
                          ⏳ {Math.round((schedulingStatus.lastEmailSent.timestamp - Date.now()) / 60000)} min remaining
                        </div>
                      )}
                      {schedulingStatus.lastEmailSent.scheduledCount > 0 && (
                        <div style={{ fontSize: '11px', color: '#6366f1', marginTop: '2px' }}>
                          📋 {schedulingStatus.lastEmailSent.scheduledCount} email(s) in queue
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#10b981', fontWeight: '500' }}>✓ Ready to send</span>
                  )}
                </div>
              </div>
              
              {/* Last Reply Sent */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '8px',
                fontSize: '13px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>↩️</span>
                  <span style={{ fontWeight: '500' }}>Next Reply Available:</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {schedulingStatus.lastReplySent ? (
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        color: schedulingStatus.lastReplySent.timestamp > Date.now() ? '#f59e0b' : '#10b981'
                      }}>
                        {new Date(schedulingStatus.lastReplySent.timestamp).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      {schedulingStatus.lastReplySent.timestamp > Date.now() && (
                        <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>
                          ⏳ {Math.round((schedulingStatus.lastReplySent.timestamp - Date.now()) / 60000)} min remaining
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#10b981', fontWeight: '500' }}>✓ Ready to send</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
          <strong>What gets reset:</strong><br/>
          • Last email sent timestamps (lastEmailSent)<br/>
          • Last reply sent timestamps (lastReplySent)<br/>
          <br/>
          <strong>Note:</strong> Already scheduled emails in Cloud Tasks will still be sent, 
          but new emails won't wait for them.
        </p>
        
        <div className="resetSchedulingContainer" style={{ marginTop: '15px' }}>
          {schedulingResetSuccess ? (
            <div className="resetSuccessMessage" style={{ 
              padding: '12px 16px', 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
              borderRadius: '8px', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>✅</span>
              <span>Scheduling queue successfully reset!</span>
            </div>
          ) : (
            <button
              className="resetSchedulingButton"
              onClick={handleResetEmailScheduling}
              disabled={isResettingScheduling}
              style={{
                background: isResettingScheduling 
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' 
                  : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isResettingScheduling ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              {isResettingScheduling ? (
                <>
                  <span className="spinner" style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid rgba(255,255,255,0.3)', 
                    borderTop: '2px solid white', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></span>
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '16px' }}>🔄</span>
                  <span>Reset Scheduling Queue</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Funktion zum Speichern der AI-Einstellungen
  const saveAiSettings = async () => {
    setMessage("");
    setLoading(true);
    
    try {
      await update(ref(database, `/World/${userId}/settings/ai`), aiSettings);
      setMessage("AI settings successfully saved!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving AI settings:", error);
      setMessage("Error saving AI settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };


  // Funktion zum Zurücksetzen der Reply Agent Guidelines auf den Standardwert
  const resetReplyAgentGuidelines = () => {
    setAiSettings(prev => ({
      ...prev,
      replyAgentGuidelines: `- Analyze the incoming email's tone and enthusiasm level
- Match the sender's communication style and formality level
- Reference specific points mentioned in the original email
- Keep responses concise and focused on the main topic
- Show genuine interest in their business or inquiry
- Provide helpful information without being overly promotional
- Use natural language that sounds conversational
- Address any questions or concerns directly
- Maintain professional boundaries while being friendly
- End with a clear next step or invitation for further discussion`
    }));
  };

  // Neue Funktion für den Agents Tab
  const renderAgentsTab = () => (
    <AgentsSettings userId={userId} />
  );

  if (!userId) {
    return <p className="errorMessage">Please login to save settings.</p>;
  }

  // 🔒 SICHERHEIT: Team-Mitglied ohne Settings-Berechtigung
  if (!isTeamLoading && accountType === 'member' && !hasSettingsAccess) {
    return (
      <div className="worldSettingsContainer" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh',
        textAlign: 'center',
        padding: '40px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '1px solid #fca5a5',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: '#dc2626', marginBottom: '12px', fontSize: '24px' }}>Access Restricted</h2>
          <p style={{ color: '#7f1d1d', fontSize: '16px', lineHeight: '1.6' }}>
            You don't have permission to access the Settings area.<br/>
            Please contact your team administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="worldSettingsContainer">
      {loading ? (
        <div className="world-settings-loading">
          <div className="settings-salesgod-loading-container">
            <div className="settings-salesgod-loading-logo">
              <img src="/img/salesgod_dark.webp" alt="SalesGod" className="settings-salesgod-logo-image" />
            </div>
            <div className="settings-salesgod-loading-text">Loading settings...</div>
          </div>
        </div>
      ) : (
        <div className="settingsForm">
          <div className="settingsTabs">
            <button 
              className={`settingsTab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <div className="settingsTabIcon settingsTabIcon--cockpit"><i className="fa fa-tachometer-alt"></i></div>
              <span className="settingsTabText">Cockpit</span>
            </button>
            {/* Email Section */}
            <button 
              className={`settingsTab settingsTabParent ${emailExpanded ? 'expanded' : ''} ${EMAIL_TABS.includes(activeTab) ? 'parentActive' : ''} ${activeTab === 'emailOutreach' ? 'active' : ''}`}
              onClick={() => {
                if (activeTab === 'emailOutreach') {
                  if (emailExpanded) setEmailExpanded(false);
                  else openNavSection('email');
                } else {
                  setActiveTab('emailOutreach');
                }
              }}
            >
              <div className="settingsTabIcon settingsTabIcon--email"><i className="fa fa-envelope"></i></div>
              <span className="settingsTabText">
                Email
                <span className="settingsTabBadgeAi">Outreach</span>
              </span>
              <span className={`settingsTabArrow ${emailExpanded ? 'rotated' : ''}`}>▾</span>
            </button>
            <AnimatePresence initial={false}>
            {emailExpanded && (
              <motion.div key="email-sub" {...SUBNAV_MOTION}>
                <button 
                  className={`settingsTab settingsSubTab ${EMAIL_SETUP_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
                  onClick={() => {
                    if (!EMAIL_SETUP_TAB_IDS.includes(activeTab)) setActiveTab(EMAIL_SETUP_TAB_IDS[0]);
                  }}
                >
                  <span className="settingsTabText">Email Setup</span>
                </button>
                <button 
                  className={`settingsTab settingsSubTab ${TEMPLATE_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
                  onClick={() => {
                    if (!TEMPLATE_TAB_IDS.includes(activeTab)) setActiveTab(TEMPLATE_TAB_IDS[0]);
                  }}
                >
                  <span className="settingsTabText">Templates</span>
                </button>
                <button
                  className={`settingsTab settingsSubTab ${activeTab === 'agents' ? 'active' : ''}`}
                  onClick={() => setActiveTab('agents')}
                >
                  <span className="settingsTabText">Inbox Auto-Reply</span>
                </button>
              </motion.div>
            )}
            </AnimatePresence>
            {/* Agents & Flows Section */}
            <button 
              className={`settingsTab settingsTabParent ${agentsFlowsExpanded ? 'expanded' : ''} ${AGENTS_FLOWS_TABS.includes(activeTab) ? 'parentActive' : ''} ${activeTab === 'aiStudio' ? 'active' : ''}`}
              onClick={() => {
                if (activeTab === 'aiStudio') {
                  if (agentsFlowsExpanded) setAgentsFlowsExpanded(false);
                  else openNavSection('agentsFlows');
                } else {
                  setActiveTab('aiStudio');
                }
              }}
            >
              <div className="settingsTabIcon settingsTabIcon--intelligence">
                <img src="/img/star.svg" alt="" />
              </div>
              <span className="settingsTabText">
                AI
                <span className="settingsTabBadgeAi">Tools</span>
              </span>
              <span className={`settingsTabArrow ${agentsFlowsExpanded ? 'rotated' : ''}`}>▾</span>
            </button>
            <AnimatePresence initial={false}>
            {agentsFlowsExpanded && (
              <motion.div key="agentsFlows-sub" {...SUBNAV_MOTION}>
                <button
                  className={`settingsTab settingsSubTab ${activeTab === 'research' ? 'active' : ''}`}
                  onClick={() => setActiveTab('research')}
                >
                  <span className="settingsTabText">Clean &amp; Enrich</span>
                </button>
                <button
                  className={`settingsTab settingsSubTab ${SALES_STUDIO_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
                  onClick={() => setActiveTab('salesStudio')}
                >
                  <span className="settingsTabText">Sales Bots</span>
                </button>
                <button 
                  className={`settingsTab settingsSubTab ${activeTab === 'leadSettings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('leadSettings')}
                >
                  <span className="settingsTabText">Flows</span>
                </button>
                {hasOutreachJobs && (
                  <button
                    className={`settingsTab settingsSubTab ${activeTab === 'outreachJobs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('outreachJobs')}
                  >
                    <span className="settingsTabText">Background Agents</span>
                  </button>
                )}
              </motion.div>
            )}
            </AnimatePresence>

            <button
              className={`settingsTab ${SAFETY_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
              onClick={() => {
                if (!SAFETY_TAB_IDS.includes(activeTab)) setActiveTab(SAFETY_TAB_IDS[0]);
              }}
            >
              <div className="settingsTabIcon settingsTabIcon--safety"><i className="fa fa-shield-alt"></i></div>
              <span className="settingsTabText">
                Safety
                <span className="settingsTabHint">SafeGuard · BlackList · More</span>
              </span>
            </button>
            {/* Admin Section */}
            <button 
              className={`settingsTab settingsTabParent ${adminExpanded ? 'expanded' : ''} ${(ADMIN_TABS.includes(activeTab) || INTEGRATION_TABS.includes(activeTab)) ? 'parentActive' : ''}`}
              onClick={() => {
                if (adminExpanded) setAdminExpanded(false);
                else openNavSection('admin');
              }}
            >
              <div className="settingsTabIcon settingsTabIcon--admin"><i className="fa fa-cog"></i></div>
              <span className="settingsTabText">
                Admin
                <span className="settingsTabHint">API · Team · Analytics</span>
              </span>
              <span className={`settingsTabArrow ${adminExpanded ? 'rotated' : ''}`}>▾</span>
            </button>
            <AnimatePresence initial={false}>
            {adminExpanded && (
              <motion.div key="admin-sub" {...SUBNAV_MOTION}>
                {/* Integrations — API / CRM / Calendly / WhatsApp teilen sich
                    einen Sidebar-Eintrag mit 4er-Pane-Nav (siehe Safety). */}
                <button
                  className={`settingsTab settingsSubTab ${INTEGRATIONS_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
                  onClick={() => {
                    if (!INTEGRATIONS_TAB_IDS.includes(activeTab)) setActiveTab(INTEGRATIONS_TAB_IDS[0]);
                  }}
                >
                  <span className="settingsTabText">API & Integrations</span>
                </button>
                {isOwner && (
                  <button 
                    className={`settingsTab settingsSubTab ${activeTab === 'team' ? 'active' : ''}`}
                    onClick={() => setActiveTab('team')}
                  >
                    <span className="settingsTabText">Team</span>
                  </button>
                )}
                <button 
                  className={`settingsTab settingsSubTab ${activeTab === 'analytics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analytics')}
                >
                  <span className="settingsTabText">Analytics</span>
                </button>
                <button
                  className={`settingsTab settingsSubTab ${ADVANCED_SETTINGS_TAB_IDS.includes(activeTab) ? 'active' : ''}`}
                  onClick={() => {
                    if (!ADVANCED_SETTINGS_TAB_IDS.includes(activeTab)) {
                      setActiveTab(isOwner ? ADVANCED_SETTINGS_TAB_IDS[0] : 'ai');
                    }
                  }}
                >
                  <span className="settingsTabText">Advanced Settings</span>
                </button>
                {/* Danger Zone - nur für Owner sichtbar */}
                {isOwner && (
                  <button 
                    className={`settingsTab settingsSubTab dangerTab ${activeTab === 'dangerZone' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dangerZone')}
                  >
                    <span className="settingsTabText">Danger Zone</span>
                  </button>
                )}
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          <div className={`settingsContent${activeTab === 'dashboard' ? ' settingsContent--cockpit' : ''}`} key={activeTab}>
            {activeTab === 'dashboard' ? <WorldSettingsCockpit onOpenTab={setActiveTab} /> :
            activeTab === 'aiStudio' ? <AiStudioOverview onOpenTab={setActiveTab} hasOutreachJobs={hasOutreachJobs} /> :
            activeTab === 'emailOutreach' ? <EmailOutreachOverview onOpenTab={setActiveTab} /> :
            ADVANCED_SETTINGS_TAB_IDS.includes(activeTab) ? renderAdvancedSettingsGroup() :
            INTEGRATIONS_TAB_IDS.includes(activeTab) ? renderIntegrationsGroup() :
            activeTab === 'agents' ? renderAgentsTab() :
            EMAIL_SETUP_TAB_IDS.includes(activeTab) ? renderEmailSetup() :
            TEMPLATE_TAB_IDS.includes(activeTab) ? renderTemplatesGroup() :
            SALES_STUDIO_TAB_IDS.includes(activeTab) ? <SalesStudio userId={userId} initialView={activeTab} /> :
            activeTab === 'whisper' ? renderWhisperTab() :
            activeTab === 'dangerZone' && isOwner ? renderDangerZoneTab() :
            SAFETY_TAB_IDS.includes(activeTab) ? renderSafetyGroup() :
            activeTab === 'leadSettings' ? <LeadSettings userId={userId} /> :
            activeTab === 'research' ? <ResearchSettingsPage userId={userId} /> :
            activeTab === 'outreachJobs' && hasOutreachJobs ? <OutreachJobsSettings userId={userId} /> :
            activeTab === 'analytics' ? <AnalyticsSettings key="analytics-tab" userId={userId} /> :
            activeTab === 'team' && isOwner ? <TeamSettings userId={userId} /> :
            activeTab === 'screenComposer' ? <ImageLab userId={userId} tool="screen" /> : null}

            {activeTab !== 'dashboard' && activeTab !== 'aiStudio' && activeTab !== 'emailOutreach' && activeTab !== 'dangerZone' && activeTab !== 'whatsapp' && activeTab !== 'whisper' && activeTab !== 'email' && activeTab !== 'emailPreferences' && activeTab !== 'copyClones' && activeTab !== 'topics' && activeTab !== 'imageLab' && activeTab !== 'salesStudio' && activeTab !== 'agents' && activeTab !== 'extraSettings' && activeTab !== 'safeGuard' && activeTab !== 'spamAnalyzer' && activeTab !== 'blacklist' && activeTab !== 'leadSettings' && activeTab !== 'research' && activeTab !== 'outreachJobs' && activeTab !== 'analytics' && activeTab !== 'team' && activeTab !== 'api' && activeTab !== 'trueWarmup' && activeTab !== 'customFields' && activeTab !== 'screenComposer' && activeTab !== 'crmIntegrations' && activeTab !== 'dataExport' && activeTab !== 'autoForward' && (
              <div className="settingActions">
                <button 
                  className={`saveSettingsButton ${isSaved ? 'saved' : ''}`} 
                  onClick={handleSave}
                  style={{
                    backgroundColor: isSaved ? '#10b981' : 'var(--blue)',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  {isSaved ? 'Saved!' : 'Save Settings'}
                </button>
              </div>
            )}

                         {message && <p className="world_settings_message">{message}</p>}
           </div>
        </div>
      )}
      
      {/* Turbo AI Setup Modal */}
      <TurboAiSetup
        isOpen={showTurboSetup}
        onClose={() => setShowTurboSetup(false)}
        userId={userId}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default WorldSettings;
